import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ChartBarIcon,
  UserGroupIcon,
  DocumentTextIcon,
  PlayIcon,
  ClipboardDocumentListIcon,
  MagnifyingGlassIcon,
  CubeIcon,
  ClockIcon,
  CheckIcon,
  RocketLaunchIcon,
  CalendarDaysIcon,
  TagIcon,
  UserIcon,
  XMarkIcon,
  PlusIcon,
  CheckCircleIcon,
  PaperClipIcon,
  TrashIcon,
  FolderIcon,
  ArrowPathIcon,
  EyeIcon,
  PencilSquareIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { GoogleDriveFilePicker } from "../components/workflow/GoogleDriveFilePicker";
import { PaginationControls } from "../components/common/PaginationControls";
import { useAuth } from "../contexts/AuthContext";
import { apiService } from "../services/apiService";
import { Workflow, GoogleDriveAttachment, UserRole } from "../types";
import toast from "../utils/toast";
import AddActivityModal from "../components/workflow/AddActivityModal";
import UploadFilesToProjectModal from "../components/workflow/UploadFilesToProjectModal";
import ProjectFolderSelectModal from "../components/workflow/ProjectFolderSelectModal";
import { useTranslation } from "react-i18next";
import { TaskDetailModal } from "../components/reports/TaskDetailModal";

interface ActivityInstance {
  id: number;
  name: string;
  description: string;
  status:
    | "PENDING"
    | "ACTIVE"
    | "IN_PROGRESS"
    | "COMPLETED"
    | "DELETED"
    | "PENDING_APPROVAL"
    | "REJECTED";
  assignedUsers: string;
  completedUserIds?: string; // JSON array: "[1,5,12]"
  startedAt?: string;
  completedAt?: string;
  workflowInstanceId: number;
  workflowNodeId?: string;
  userFormData?: string; // JSON object: {"userId": {formData}}
  userDurations?: string; // JSON object: {"userId": duration}
  // From template Activity
  activityTitle?: string;
  activityDescription?: string;
  startDate?: string;
  endDate?: string;
  estimatedDurationMinutes?: number;
  tags?: string;
  icon?: string;
  version?: number;
  requiresApproval?: boolean;
  // Forms
  forms?: Array<{
    id: number;
    templateFormId?: number;
    title: string;
    description?: string;
    formStructure: any;
    submissionData?: any; // Form template's content/values
    userResponse?: any; // User's input from dashboard
    isRequired?: boolean;
    displayOrder?: number;
  }>;
  // Additional info
  workflowInstanceName?: string;
  projectName?: string;
  assignedUsersList?: Array<{
    id: number;
    firstName: string;
    lastName: string;
    username: string;
    email: string;
  }>;
  // Attachments
  attachments?: string; // JSON array of GoogleDriveAttachment objects
  // History fields
  isAdHoc?: boolean;
  addedByUserId?: number;
  addedAt?: string;
  // Submit description
  submitDescription?: string;
}

type ActivityManagerCommentNotification = {
  id: number;
  activityInstanceId: number;
  comment: string;
  createdAt?: string | null;
  authorName?: string | null;
  activityTitle?: string | null;
  projectName?: string | null;
  workflowInstanceId?: number | null;
  startTime?: string | null;
  endTime?: string | null;
};

type BreakEntry = {
  startTime: string;
  endTime: string;
};

type CompleteModalPathEntry = {
  id: string | null;
  name: string;
};

type CompleteModalLocalUploadFile = File & {
  webkitRelativePath?: string;
};

type CompleteModalSelectedFile = {
  id: string;
  file: CompleteModalLocalUploadFile;
  folderId: string | null;
  folderPath: string;
  folderPathEntries: CompleteModalPathEntry[];
};

type CompleteModalSelectedDriveAttachment = GoogleDriveAttachment & {
  targetFolderId: string | null;
  targetFolderPath: string;
  targetFolderPathEntries: CompleteModalPathEntry[];
};

type DailyHoursStatus = {
  dailyTargetHours?: number;
  dailyWorkedHours?: number;
  dailyRemainingHours?: number;
  dailyOverHours?: number;
  isWorkday?: boolean;
  isHalfDay?: boolean;
};

const isManualAssignedTaskActivity = (activity: ActivityInstance): boolean =>
  typeof activity.workflowNodeId === "string" &&
  activity.workflowNodeId.startsWith("manual-assign-");

const allowsZeroDurationForTaskActivity = (
  activity: ActivityInstance | null | undefined
): boolean => Boolean(activity && isManualAssignedTaskActivity(activity));

const hasMeaningfulBreakEntry = (breaks: BreakEntry[]): boolean =>
  breaks.some(
    (entry) => Boolean(entry.startTime?.trim()) || Boolean(entry.endTime?.trim())
  );

const shouldNormalizeZeroDurationTimeEntry = (
  activity: ActivityInstance | null | undefined,
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string,
  breaks: BreakEntry[]
): boolean =>
  allowsZeroDurationForTaskActivity(activity) &&
  !hasMeaningfulBreakEntry(breaks) &&
  Boolean(startDate && startTime && endDate && endTime) &&
  startDate === endDate &&
  startTime === endTime;

const formatLocalDatePart = (value: Date): string =>
  `${value.getFullYear()}-${(value.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${value.getDate().toString().padStart(2, "0")}`;

const formatLocalTimePart = (value: Date): string =>
  `${value.getHours().toString().padStart(2, "0")}:${value
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;

const parseDateTimeSource = (value?: string | Date | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const roundDateToNextFiveMinutes = (value: Date): Date => {
  const rounded = new Date(value);
  const hasSubMinutePrecision =
    rounded.getSeconds() > 0 || rounded.getMilliseconds() > 0;
  const minuteRemainder = rounded.getMinutes() % 5;

  rounded.setSeconds(0, 0);

  if (minuteRemainder === 0 && !hasSubMinutePrecision) {
    return rounded;
  }

  const minutesToAdd = minuteRemainder === 0 ? 5 : 5 - minuteRemainder;
  rounded.setMinutes(rounded.getMinutes() + minutesToAdd);
  return rounded;
};

const getRoundedDateTimeParts = (
  value?: string | Date | null
): { date: string; time: string } | null => {
  const parsed = parseDateTimeSource(value);
  if (!parsed) return null;

  const rounded = roundDateToNextFiveMinutes(parsed);
  return {
    date: formatLocalDatePart(rounded),
    time: formatLocalTimePart(rounded),
  };
};

const ACTIVE_ACTIVITIES_PAGE_SIZE = 10;
const DASHBOARD_HISTORY_PAGE_SIZE = 10;

export const Dashboard: React.FC = () => {
  const { t } = useTranslation([
    "dashboard",
    "tasks",
    "activities",
    "workflows",
    "common",
    "errors",
    "projects",
  ]);
  const {
    user,
    isAdmin,
    isManager,
    getSubmitDescriptionLabel,
    getSubmitDescriptionPlaceholder,
  } = useAuth();
  const projectFilesRootLabel = t("projects:projectFilesModal.rootFolder");
  const canDeleteActivityInstance = isAdmin() || isManager();
  const canBypassActivityEditWindow = isAdmin() || isManager();
  const submitDescriptionLabel = getSubmitDescriptionLabel(
    "Gönderim Açıklaması",
  );
  const submitDescriptionPlaceholder = getSubmitDescriptionPlaceholder(
    "Örn: İşi tamamladım, müşteriye gönderildi...",
  );

  // Generate time options in 15-minute intervals (00:00 - 23:45)
  const generateTimeOptions = (): string[] => {
    const times: string[] = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const h = hour.toString().padStart(2, "0");
        const m = minute.toString().padStart(2, "0");
        times.push(`${h}:${m}`);
      }
    }
    return times;
  };
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [activityInstances, setActivityInstances] = useState<
    ActivityInstance[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [historySearchTerm, setHistorySearchTerm] = useState("");
  const [selectedActivityForms, setSelectedActivityForms] =
    useState<ActivityInstance | null>(null);
  const [selectedHistoryActivity, setSelectedHistoryActivity] =
    useState<ActivityInstance | null>(null);
  const [descriptionInfoModal, setDescriptionInfoModal] = useState<{
    title: string;
    description: string;
  } | null>(null);
  const [selectedActivityAttachments, setSelectedActivityAttachments] =
    useState<{
      activity: ActivityInstance;
      attachments: GoogleDriveAttachment[];
    } | null>(null);
  const [previewingDriveFileId, setPreviewingDriveFileId] = useState<string | null>(
    null,
  );
  const [showDurationModal, setShowDurationModal] = useState(false);
  const [selectedActivityForDuration, setSelectedActivityForDuration] =
    useState<ActivityInstance | null>(null);
  const [durationInput, setDurationInput] = useState("");
  const [durationError, setDurationError] = useState<string>("");
  const [formSubmissions, setFormSubmissions] = useState<Record<number, any>>(
    {}
  );
  const [selectSearchByField, setSelectSearchByField] = useState<
    Record<string, string>
  >({});
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  const [activityToComplete, setActivityToComplete] = useState<{
    id: number;
    name: string;
    duration: number;
    formCount: number;
  } | null>(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [activityToCompleteData, setActivityToCompleteData] =
    useState<ActivityInstance | null>(null);
  const [completeModalStartTime, setCompleteModalStartTime] = useState("");
  const [completeModalEndTime, setCompleteModalEndTime] = useState("");
  const [completeModalStartDate, setCompleteModalStartDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [completeModalEndDate, setCompleteModalEndDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [completeModalBreaks, setCompleteModalBreaks] = useState<
    BreakEntry[]
  >([]);
  const [completeModalFormSubmissions, setCompleteModalFormSubmissions] =
    useState<Record<number, any>>({});
  const [completeModalSubmitDescription, setCompleteModalSubmitDescription] =
    useState("");
  const [existingSubmitDescriptions, setExistingSubmitDescriptions] = useState<
    any[]
  >([]);
  const [activityHistory, setActivityHistory] = useState<ActivityInstance[]>(
    []
  );
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyFilter, setHistoryFilter] = useState<string>("all");
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [activeActivitiesPage, setActiveActivitiesPage] = useState(1);
  const [historyActivitiesPage, setHistoryActivitiesPage] = useState(1);

  const [unreadManagerComments, setUnreadManagerComments] = useState<
    ActivityManagerCommentNotification[]
  >([]);
  const [loadingManagerComments, setLoadingManagerComments] = useState(false);

  // File upload state for complete modal
  const [completeModalFiles, setCompleteModalFiles] = useState<
    CompleteModalSelectedFile[]
  >([]);
  const [completeModalDriveAttachments, setCompleteModalDriveAttachments] =
    useState<CompleteModalSelectedDriveAttachment[]>([]);
  const [completeModalFileDescriptions, setCompleteModalFileDescriptions] =
    useState<Record<string, string>>({});
  const [completeModalSelectedFolderId, setCompleteModalSelectedFolderId] =
    useState<string | null>(null);
  const [completeModalSelectedFolderPath, setCompleteModalSelectedFolderPath] =
    useState<string>(projectFilesRootLabel);
  const [
    completeModalSelectedFolderPathEntries,
    setCompleteModalSelectedFolderPathEntries,
  ] = useState<CompleteModalPathEntry[]>([
    { id: null, name: projectFilesRootLabel },
  ]);
  const [completeModalRootFolderNames, setCompleteModalRootFolderNames] =
    useState<Record<number, string>>({});
  const [showCompleteFolderSelectModal, setShowCompleteFolderSelectModal] =
    useState(false);
  const [
    completeModalExistingAttachments,
    setCompleteModalExistingAttachments,
  ] = useState<GoogleDriveAttachment[]>([]);
  const [
    completeModalRemovedAttachmentIds,
    setCompleteModalRemovedAttachmentIds,
  ] = useState<string[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activityToDelete, setActivityToDelete] =
    useState<ActivityInstance | null>(null);
  const [showDrivePicker, setShowDrivePicker] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const completeModalFileInputRef = useRef<HTMLInputElement | null>(null);
  const completeModalFolderInputRef = useRef<HTMLInputElement | null>(null);
  const completeModalSelectedFolderIdRef = useRef<string | null>(null);
  const completeModalSelectedFolderPathEntriesRef = useRef<
    CompleteModalPathEntry[]
  >([{ id: null, name: projectFilesRootLabel }]);
  const completeModalActivityIdRef = useRef<number | null>(null);

  // Modal state
  const [showAddActivityModal, setShowAddActivityModal] = useState(false);
  const [showUploadFilesModal, setShowUploadFilesModal] = useState(false);
  const [dailyHoursStatus, setDailyHoursStatus] =
    useState<DailyHoursStatus | null>(null);

  const handlePreviewWorkflowAttachment = useCallback(
    async (attachment: GoogleDriveAttachment) => {
      if (!attachment?.id) return;

      const previewWindow = apiService.shouldOpenPreviewWindow(
        attachment.name,
        attachment.mimeType,
      )
        ? window.open("", "_blank")
        : null;
      if (previewWindow) {
        previewWindow.opener = null;
        apiService.preparePreviewWindow(previewWindow, attachment.name);
      }

      try {
        setPreviewingDriveFileId(attachment.id);
        await apiService.previewGoogleDriveFile(
          attachment.id,
          attachment.name || "preview",
          previewWindow,
        );
      } catch (error) {
        if (previewWindow && !previewWindow.closed) {
          previewWindow.close();
        }
        toast.error("Dosya görüntülenemedi.");
      } finally {
        setPreviewingDriveFileId(null);
      }
    },
    [],
  );

  const getCompleteModalRootLabel = (
    activity?: ActivityInstance | null
  ): string =>
    (activity?.workflowInstanceId
      ? completeModalRootFolderNames[activity.workflowInstanceId]
      : undefined) ||
    activity?.projectName ||
    activity?.workflowInstanceName ||
    projectFilesRootLabel;

  const buildCompleteModalRootPathEntries = (
    rootLabel: string
  ): CompleteModalPathEntry[] => [{ id: null, name: rootLabel }];

  const createCompleteModalFileId = (file: File): string =>
    `${file.name}-${file.size}-${file.lastModified}-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;

  const normalizeCompleteModalFolderName = (value?: string | null): string =>
    String(value || "").trim().toLocaleLowerCase("tr-TR");

  const getCompleteModalLocalFileRelativeFolderPath = (file: File): string => {
    const relativePath = (file as CompleteModalLocalUploadFile).webkitRelativePath
      ?.replace(/\\/g, "/")
      .trim();
    if (!relativePath) {
      return "";
    }
    const segments = relativePath.split("/").filter(Boolean);
    segments.pop();
    return segments.join("/");
  };

  const getCompleteModalLocalFileRootFolderName = (file: File): string => {
    const relativePath = (file as CompleteModalLocalUploadFile).webkitRelativePath
      ?.replace(/\\/g, "/")
      .trim();
    if (!relativePath) {
      return "";
    }
    return relativePath.split("/").filter(Boolean)[0] || "";
  };

  const getCompleteModalLocalFileDisplayPath = (file: File): string => {
    const relativePath = (file as CompleteModalLocalUploadFile).webkitRelativePath
      ?.replace(/\\/g, "/")
      .trim();
    return relativePath || file.name;
  };

  const getCompleteModalFileTargetFolderDisplayPath = (
    selectedFile: CompleteModalSelectedFile
  ): string => {
    const basePath =
      selectedFile.folderPath ||
      selectedFile.folderPathEntries
        .map((entry) => entry.name)
        .filter(Boolean)
        .join(" / ") ||
      getCompleteModalRootLabel(activityToCompleteData);
    const relativeFolderPath = getCompleteModalLocalFileRelativeFolderPath(
      selectedFile.file
    );
    if (!relativeFolderPath) {
      return basePath;
    }
    return `${basePath} / ${relativeFolderPath
      .split("/")
      .filter(Boolean)
      .join(" / ")}`;
  };

  const buildUniqueCompleteModalFolderName = (
    baseName: string,
    existingFolderNames: Set<string>
  ): string => {
    const trimmedBaseName = baseName.trim() || "Klasör";
    let candidate = trimmedBaseName;
    let suffix = 2;

    while (
      existingFolderNames.has(normalizeCompleteModalFolderName(candidate))
    ) {
      candidate = `${trimmedBaseName} (${suffix})`;
      suffix += 1;
    }

    existingFolderNames.add(normalizeCompleteModalFolderName(candidate));
    return candidate;
  };

  const buildSelectedCompleteModalFiles = (
    files: File[]
  ): CompleteModalSelectedFile[] =>
    files.map((file) => ({
      id: createCompleteModalFileId(file),
      file: file as CompleteModalLocalUploadFile,
      folderId: completeModalSelectedFolderId,
      folderPath: completeModalSelectedFolderPath,
      folderPathEntries: [...completeModalSelectedFolderPathEntries],
    }));

  const buildSelectedCompleteModalDriveAttachments = (
    attachments: GoogleDriveAttachment[]
  ): CompleteModalSelectedDriveAttachment[] =>
    attachments.map((attachment) => ({
      ...attachment,
      targetFolderId: completeModalSelectedFolderId,
      targetFolderPath: completeModalSelectedFolderPath,
      targetFolderPathEntries: [...completeModalSelectedFolderPathEntries],
    }));

  useEffect(() => {
    completeModalSelectedFolderIdRef.current = completeModalSelectedFolderId;
  }, [completeModalSelectedFolderId]);

  useEffect(() => {
    completeModalSelectedFolderPathEntriesRef.current =
      completeModalSelectedFolderPathEntries;
  }, [completeModalSelectedFolderPathEntries]);

  const loadCompleteModalRootFolderName = async (
    activity?: ActivityInstance | null
  ): Promise<string | null> => {
    if (!activity?.workflowInstanceId) {
      return null;
    }

    const cachedName = completeModalRootFolderNames[activity.workflowInstanceId];
    if (cachedName) {
      return cachedName;
    }

    try {
      const workflowInstance = await apiService.getWorkflowInstanceById(
        activity.workflowInstanceId
      );
      const folderId = workflowInstance?.googleDriveFolderId?.trim();
      if (!folderId) {
        return null;
      }

      const folderInfo = await apiService.getGoogleDriveFileInfo(folderId);
      const folderName = folderInfo?.name?.trim();
      if (folderName) {
        setCompleteModalRootFolderNames((prev) => ({
          ...prev,
          [activity.workflowInstanceId]: folderName,
        }));
        return folderName;
      }
    } catch (error) {
    }

    return null;
  };

  const syncCompleteModalRootFolderLabel = (activity: ActivityInstance) => {
    const fallbackRootLabel =
      completeModalRootFolderNames[activity.workflowInstanceId] ||
      activity.projectName ||
      activity.workflowInstanceName ||
      projectFilesRootLabel;

    completeModalActivityIdRef.current = activity.id;
    setCompleteModalSelectedFolderId(null);
    setCompleteModalSelectedFolderPath(fallbackRootLabel);
    setCompleteModalSelectedFolderPathEntries(
      buildCompleteModalRootPathEntries(fallbackRootLabel)
    );

    void loadCompleteModalRootFolderName(activity).then((resolvedRootLabel) => {
      if (
        !resolvedRootLabel ||
        completeModalActivityIdRef.current !== activity.id
      ) {
        return;
      }

      const currentFolderId = completeModalSelectedFolderIdRef.current;
      const currentPathEntries = completeModalSelectedFolderPathEntriesRef.current;
      const currentRootLabel = currentPathEntries[0]?.name || "";
      const isStillAtRoot =
        currentFolderId === null && currentPathEntries.length === 1;

      if (!isStillAtRoot || currentRootLabel !== fallbackRootLabel) {
        return;
      }

      setCompleteModalSelectedFolderPath(resolvedRootLabel);
      setCompleteModalSelectedFolderPathEntries(
        buildCompleteModalRootPathEntries(resolvedRootLabel)
      );
    });
  };

  const removeCompleteModalFile = (fileId: string) => {
    setCompleteModalFiles((prev) => prev.filter((file) => file.id !== fileId));
    setCompleteModalFileDescriptions((prev) => {
      const next = { ...prev };
      delete next[fileId];
      return next;
    });
  };

  const removeCompleteModalDriveFile = (fileId: string) => {
    setCompleteModalDriveAttachments((prev) =>
      prev.filter((file) => file.id !== fileId)
    );
    setCompleteModalFileDescriptions((prev) => {
      const next = { ...prev };
      delete next[fileId];
      return next;
    });
  };

  const addCompleteModalFiles = (files: FileList | File[]) => {
    if (!files || files.length === 0) {
      return;
    }
    const normalizedFiles = Array.from(files);
    if (normalizedFiles.length === 0) {
      return;
    }
    setCompleteModalFiles((prev) => [
      ...prev,
      ...buildSelectedCompleteModalFiles(normalizedFiles),
    ]);
  };

  const handleCompleteModalFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const normalizedFiles = Array.from(files);
      event.target.value = "";
      addCompleteModalFiles(normalizedFiles);
    }
  };

  const handleCompleteModalFolderSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const normalizedFiles = Array.from(files);
      event.target.value = "";
      addCompleteModalFiles(normalizedFiles);
    }
  };

  const resolveUploadFolderIdForCompleteModalFile = async (
    workflowInstanceId: number,
    selectedFile: CompleteModalSelectedFile,
    folderCache: Map<string, string | null>,
    rootFolderNameCache: Map<string, string>
  ): Promise<string | null> => {
    const relativeFolderPath = getCompleteModalLocalFileRelativeFolderPath(
      selectedFile.file
    );
    if (!relativeFolderPath) {
      return selectedFile.folderId;
    }

    const segments = relativeFolderPath.split("/").filter(Boolean);
    const baseFolderKey = selectedFile.folderId ?? "__root__";
    let currentParentId = selectedFile.folderId ?? null;
    let currentPath = "";

    for (const segment of segments) {
      currentPath = currentPath ? `${currentPath}/${segment}` : segment;
      const cacheKey = `${baseFolderKey}::${currentPath}`;
      if (folderCache.has(cacheKey)) {
        currentParentId = folderCache.get(cacheKey) ?? null;
        continue;
      }

      let folderNameToCreate = segment;
      if (!currentPath.includes("/")) {
        const rootFolderName = getCompleteModalLocalFileRootFolderName(
          selectedFile.file
        );
        if (rootFolderName) {
          const rootFolderCacheKey = `${baseFolderKey}::${normalizeCompleteModalFolderName(
            rootFolderName
          )}`;
          if (rootFolderNameCache.has(rootFolderCacheKey)) {
            folderNameToCreate =
              rootFolderNameCache.get(rootFolderCacheKey) || segment;
          } else {
            const existingItems = await apiService.getWorkflowInstanceFiles(
              workflowInstanceId,
              currentParentId
            );
            const existingFolderNames = new Set(
              existingItems
                .filter(
                  (existingFile) =>
                    existingFile.mimeType ===
                    "application/vnd.google-apps.folder"
                )
                .map((existingFolder) =>
                  normalizeCompleteModalFolderName(existingFolder.name)
                )
            );
            folderNameToCreate = buildUniqueCompleteModalFolderName(
              segment,
              existingFolderNames
            );
            rootFolderNameCache.set(rootFolderCacheKey, folderNameToCreate);
          }
        }
      }

      const createdFolder = await apiService.createProjectFolder(
        workflowInstanceId,
        folderNameToCreate,
        currentParentId
      );
      currentParentId = createdFolder.id;
      folderCache.set(cacheKey, currentParentId);
    }

    return currentParentId;
  };

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      if (isAdmin()) {
        const [workflowData, activitiesData, dailyHours] = await Promise.all([
          apiService.getWorkflows(),
          apiService.getActivityInstances(),
          apiService.getDailyHoursStatus(),
        ]);
        setWorkflows(workflowData);
        setActivityInstances(activitiesData);
        if (dailyHours && typeof dailyHours.isWorkday === "boolean") {
          setDailyHoursStatus(dailyHours);
        }
      } else {
        const [activitiesData, dailyHours] = await Promise.all([
          apiService.getActivityInstances(),
          apiService.getDailyHoursStatus(),
        ]);
        setActivityInstances(activitiesData);
        if (dailyHours && typeof dailyHours.isWorkday === "boolean") {
          setDailyHoursStatus(dailyHours);
        }
      }
    } catch (error) {
      toast.error(t("dashboard:dashboardDataLoadError"));
    } finally {
      setLoading(false);
    }
  }, [isAdmin, user]);

  const loadActivityHistory = useCallback(async () => {
    if (!user) return;

    try {
      setLoadingHistory(true);
      const history = await apiService.getUserActivityHistory(user.id);
      setActivityHistory(history);
    } catch (error) {
    } finally {
      setLoadingHistory(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadActivityHistory();
    }
  }, [user, loadActivityHistory]);

  const loadUnreadManagerComments = useCallback(async () => {
    if (!user) return;
    // Only show on non-admin/manager dashboards.
    if (isAdmin() || isManager()) {
      setUnreadManagerComments([]);
      return;
    }

    try {
      setLoadingManagerComments(true);
      const response = await apiService.getUnreadActivityManagerComments();
      const data = (response?.data || response) as
        | ActivityManagerCommentNotification[]
        | null;
      setUnreadManagerComments(Array.isArray(data) ? data : []);
    } catch (error) {
    } finally {
      setLoadingManagerComments(false);
    }
  }, [user, isAdmin, isManager]);

  useEffect(() => {
    if (user) {
      loadUnreadManagerComments();
    }
  }, [user, loadUnreadManagerComments]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    setActiveActivitiesPage(1);
  }, [activeSearchTerm, activeFilter, activityInstances.length]);

  useEffect(() => {
    setHistoryActivitiesPage(1);
  }, [historySearchTerm, historyFilter, activityHistory.length]);

  // Load ONLY userResponse (user's saved data) when modal opens
  // submissionData is manager's template and should NOT be loaded as initial values
  useEffect(() => {
    if (selectedActivityForms?.forms) {
      const initialValues: Record<number, any> = {};
      selectedActivityForms.forms.forEach((form) => {
        // Only load user's own saved data, NOT the template data
        if (form.userResponse && Object.keys(form.userResponse).length > 0) {
          initialValues[form.id] = { ...form.userResponse };
        }
        // If no userResponse, leave it empty (user hasn't filled it yet)
      });
      setFormSubmissions(initialValues);
    }
  }, [selectedActivityForms]);

  const getActivityStatusColor = (status: string) => {
    switch (status) {
      case "PENDING":
        return "bg-gray-100 text-gray-600"; // Locked, waiting
      case "PENDING_APPROVAL":
        return "bg-amber-100 text-amber-700";
      case "ACTIVE":
        return "bg-blue-500 text-white"; // Ready to work
      case "COMPLETED":
        return "bg-green-500 text-white";
      case "REJECTED":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleMarkManagerCommentRead = async (commentId: number) => {
    try {
      await apiService.markActivityManagerCommentRead(commentId);
      setUnreadManagerComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch (error) {
    }
  };

  const handleOpenManagerCommentDetails = async (
    item: ActivityManagerCommentNotification
  ) => {
    try {
      const activity =
        activityHistory.find((a) => a.id === item.activityInstanceId) || null;
      if (activity) {
        await handleViewDetails(activity as any);
        return;
      }

      // Fallback: refresh history once if we couldn't find it locally.
      if (user) {
        const history = await apiService.getUserActivityHistory(user.id);
        setActivityHistory(history);
        const refreshed = Array.isArray(history)
          ? history.find((a: any) => a?.id === item.activityInstanceId)
          : null;
        if (refreshed) {
          await handleViewDetails(refreshed as any);
        }
      }
    } catch (error) {
    }
  };

  const getActivityStatusLabel = (status: string) => {
    switch (status) {
      case "PENDING":
        return t("dashboard:pending");
      case "PENDING_APPROVAL":
        return t("dashboard:pendingApproval");
      case "ACTIVE":
        return t("dashboard:active");
      case "COMPLETED":
        return t("dashboard:completed");
      case "IN_PROGRESS":
        return t("dashboard:inProgress");
      case "REJECTED":
        return t("tasks:status.rejected");
      default:
        return status;
    }
  };

  const handleViewDetails = async (activity: ActivityInstance) => {
    // Map ActivityInstance fields to ActivityReport format for TaskDetailModal
    let enhancedActivity: any = {
      ...activity,
      // Map field names to match ActivityReport interface
      activityId: activity.id,
      activityTitle: activity.name,
      activityDescription: activity.description || activity.activityDescription,
      activityStatus: activity.status,
      activityCreatedAt:
        activity.startedAt || activity.addedAt || new Date().toISOString(),
      activityCompletedAt: activity.completedAt,
    };

    // Fetch detailed user forms if completed (same as Project Instance Detail)
    if (
      (activity.status === "COMPLETED" ||
        activity.status === "PENDING_APPROVAL") &&
      activity.id
    ) {
      try {
        const allUserFormsData =
          await apiService.getActivityInstanceWithAllUserForms(activity.id);

        // Transform userResponses to the format TaskDetailModal expects (allUserForms)
        const userForms =
          allUserFormsData.userResponses?.map((userResp: any) => ({
            userId: userResp.userId,
            userName: userResp.userName,
            userEmail: userResp.email,
            duration: userResp.duration,
            forms:
              userResp.forms?.map((form: any) => ({
                formId: form.formId,
                formName: form.formTitle,
                formDescription: form.formDescription,
                formStructure:
                  typeof form.formStructure === "string"
                    ? form.formStructure
                    : JSON.stringify(form.formStructure),
                userResponse:
                  typeof form.userResponse === "string"
                    ? form.userResponse
                    : JSON.stringify(form.userResponse || {}),
              })) || [],
          })) || [];

        enhancedActivity = {
          ...enhancedActivity,
          activityDescription:
            enhancedActivity.activityDescription ||
            allUserFormsData.description ||
            allUserFormsData.activityDescription ||
            activity.description ||
            activity.activityDescription ||
            "",
          allUserForms: userForms,
          submitDescription:
            allUserFormsData.submitDescription || activity.submitDescription,
          userDurations:
            allUserFormsData.userDurations || activity.userDurations,
          attachments: allUserFormsData.attachments || activity.attachments,
        };
      } catch (error) {
      }
    }

    setSelectedHistoryActivity(enhancedActivity);
  };

  const getActivityStatusIcon = (status: string) => {
    switch (status) {
      case "PENDING":
        return <ClockIcon className="w-4 h-4" />; // Waiting for dependencies
      case "PENDING_APPROVAL":
        return <ClockIcon className="w-4 h-4" />; // Waiting for approval
      case "ACTIVE":
        return <PlayIcon className="w-4 h-4" />; // Ready to start
      case "COMPLETED":
        return <CheckIcon className="w-4 h-4" />; // Done
      case "REJECTED":
        return <XMarkIcon className="w-4 h-4" />;
      default:
        return <ClockIcon className="w-4 h-4" />;
    }
  };

  const getFilteredHistoryActivities = (): ActivityInstance[] => {
    return activityHistory.filter((activity) => {
      if (activity.status === "DELETED") return false;
      // Exclude PENDING activities from history
      if (activity.status === "PENDING") return false;

      // Filter by status: show only completed or past activities
      const isCompleted = activity.status === "COMPLETED";
      const isPendingApproval = activity.status === "PENDING_APPROVAL";
      const userCompleted = activity.completedUserIds
        ? (() => {
            try {
              const completedIds: number[] = JSON.parse(
                activity.completedUserIds
              );
              return completedIds.includes(user?.id || 0);
            } catch {
              return false;
            }
          })()
        : false;

      // Only show completed activities or activities where user has completed
      const isHistoryActivity = userCompleted || isCompleted || isPendingApproval;

      if (!isHistoryActivity) return false;

      // Filter by search term
      const matchesSearch =
        historySearchTerm === "" ||
        activity.name.toLowerCase().includes(historySearchTerm.toLowerCase()) ||
        (activity.description &&
          activity.description
            .toLowerCase()
            .includes(historySearchTerm.toLowerCase()));

      if (!matchesSearch) return false;

      // Filter by project
      if (historyFilter === "all") {
        return true;
      }

      // Filter by specific project name
      return activity.projectName === historyFilter;
    });
  };

  // Get unique project names from activity history
  const getUniqueProjects = (): string[] => {
    const projects = new Set<string>();
    activityHistory.forEach((activity) => {
      if (activity.projectName) {
        projects.add(activity.projectName);
      }
    });
    return Array.from(projects).sort((a, b) => a.localeCompare(b, "tr-TR"));
  };

  // Get unique project names from active activities
  const getUniqueActiveProjects = (): string[] => {
    const projects = new Set<string>();
    activityInstances.forEach((activity) => {
      if (activity.projectName) {
        projects.add(activity.projectName);
      }
    });
    return Array.from(projects).sort((a, b) => a.localeCompare(b, "tr-TR"));
  };

  const getFilteredActivities = () => {
    return activityInstances
      .filter((activity) => {
        // Filter by search term
        const matchesSearch =
          activeSearchTerm === "" ||
          activity.name
            .toLowerCase()
            .includes(activeSearchTerm.toLowerCase()) ||
          (activity.description &&
            activity.description
              .toLowerCase()
              .includes(activeSearchTerm.toLowerCase()));

        if (!matchesSearch) return false;

        // Filter by project
        if (activeFilter === "all") {
          return true;
        }

        return activity.projectName === activeFilter;
      })
      .sort((a, b) => {
        // Sort by id (ascending) - matches backend ordering by created_at
        return a.id - b.id;
      });
  };

  // Green gradient for active activities
  const getActiveActivityGradient = () => {
    return "from-green-500 to-emerald-600";
  };

  // Purple gradient for activity history
  const getHistoryActivityGradient = () => {
    return "from-purple-500 to-indigo-600";
  };

  const getCompletionProgress = (activity: ActivityInstance) => {
    if (!activity.assignedUsers)
      return { completed: 0, total: 0, percentage: 0 };

    const assignedUsers = activity.assignedUsers
      .replace(/[\[\]\s]/g, "")
      .split(",")
      .filter((id) => id);
    const completedUsers = activity.completedUserIds
      ? activity.completedUserIds
          .replace(/[\[\]\s]/g, "")
          .split(",")
          .filter((id) => id)
      : [];

    const total = assignedUsers.length;
    const completed = completedUsers.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    return { completed, total, percentage };
  };

  const getAttachments = (
    activity: ActivityInstance
  ): GoogleDriveAttachment[] => {
    try {
      return activity.attachments ? JSON.parse(activity.attachments) : [];
    } catch (error) {
      return [];
    }
  };

  const formatDurationHours = (hours: number): number => {
    return Math.round(hours * 100) / 100;
  };

  const parseDateTimeInput = (date: string, time: string): Date | null => {
    if (!date || !time) return null;
    const value = new Date(`${date}T${time}`);
    return isNaN(value.getTime()) ? null : value;
  };

  const parseDateTimeString = (value?: string): Date | null => {
    if (!value) return null;
    const normalized = value.includes("T") ? value : value.replace(" ", "T");
    const parsed = new Date(normalized);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const getAutomaticZeroDurationTimestamp = (
    activity?: ActivityInstance | null
  ): { startTime: string; endTime: string } => {
    const referenceValue =
      (isEditMode && (activity?.completedAt || activity?.startedAt)) ||
      new Date();
    const rounded =
      getRoundedDateTimeParts(referenceValue) ||
      getRoundedDateTimeParts(new Date());

    if (!rounded) {
      const fallbackNow = new Date();
      const fallbackDate = formatLocalDatePart(fallbackNow);
      const fallbackTime = formatLocalTimePart(fallbackNow);
      return {
        startTime: `${fallbackDate} ${fallbackTime}`,
        endTime: `${fallbackDate} ${fallbackTime}`,
      };
    }

    return {
      startTime: `${rounded.date} ${rounded.time}`,
      endTime: `${rounded.date} ${rounded.time}`,
    };
  };

  const calculateDurationHours = (
    startDate: string,
    startTime: string,
    endDate: string,
    endTime: string
  ): number => {
    const start = parseDateTimeInput(startDate, startTime);
    const end = parseDateTimeInput(endDate, endTime);
    if (!start || !end) return 0;
    const diffMs = end.getTime() - start.getTime();
    if (diffMs <= 0) return 0;
    return diffMs / (1000 * 60 * 60);
  };

  const calculateBreakHoursFromRanges = (
    breaks: Array<{ start: string; end: string }>,
    rangeStart?: string,
    rangeEnd?: string
  ): number => {
    if (!breaks || breaks.length === 0) return 0;

    const activityStart = parseDateTimeString(rangeStart);
    const activityEnd = parseDateTimeString(rangeEnd);

    const intervals = breaks
      .map((entry) => {
        const start = parseDateTimeString(entry.start);
        const end = parseDateTimeString(entry.end);
        if (!start || !end || end <= start) return null;

        let adjustedStart = start;
        let adjustedEnd = end;
        if (activityStart && adjustedStart < activityStart) {
          adjustedStart = activityStart;
        }
        if (activityEnd && adjustedEnd > activityEnd) {
          adjustedEnd = activityEnd;
        }
        if (adjustedEnd <= adjustedStart) return null;
        return { start: adjustedStart, end: adjustedEnd };
      })
      .filter(Boolean) as Array<{ start: Date; end: Date }>;

    if (intervals.length === 0) return 0;

    intervals.sort((a, b) => a.start.getTime() - b.start.getTime());
    let totalMs = 0;
    let current = intervals[0];

    for (let i = 1; i < intervals.length; i += 1) {
      const next = intervals[i];
      if (next.start <= current.end) {
        if (next.end > current.end) {
          current.end = next.end;
        }
      } else {
        totalMs += current.end.getTime() - current.start.getTime();
        current = next;
      }
    }
    totalMs += current.end.getTime() - current.start.getTime();

    return totalMs / (1000 * 60 * 60);
  };

  const parseDateTimeValue = (
    value?: string,
    fallbackDate?: string
  ): { date: string; time: string } => {
    const dateMatch = value?.match(/\d{4}-\d{2}-\d{2}/);
    const timeMatch = value?.match(/\d{2}:\d{2}/);
    return {
      date: dateMatch ? dateMatch[0] : fallbackDate || "",
      time: timeMatch ? timeMatch[0] : "",
    };
  };

  const buildBreakRanges = (
    breaks: BreakEntry[],
    startDate: string,
    endDate: string
  ): Array<{ start: string; end: string }> => {
    if (!startDate || !endDate) return [];
    return breaks
      .filter((entry) => entry.startTime && entry.endTime)
      .map((entry) => {
        let endDateForEntry = startDate;
        if (startDate !== endDate) {
          const times = generateTimeOptions();
          const startIndex = times.indexOf(entry.startTime);
          const endIndex = times.indexOf(entry.endTime);
          if (startIndex !== -1 && endIndex !== -1 && endIndex < startIndex) {
            endDateForEntry = endDate;
          }
        }
        return {
          start: `${startDate} ${entry.startTime}`,
          end: `${endDateForEntry} ${entry.endTime}`,
        };
      });
  };

  const getBreakValidationError = (
    breaks: BreakEntry[],
    startDate: string,
    startTime: string,
    endDate: string,
    endTime: string
  ): string | null => {
    if (!breaks.length) return null;
    const activityStart = parseDateTimeInput(startDate, startTime);
    const activityEnd = parseDateTimeInput(endDate, endTime);
    if (!activityStart || !activityEnd) return null;

    for (const entry of breaks) {
      const hasAnyValue = entry.startTime || entry.endTime;
      if (!hasAnyValue) continue;
      if (!entry.startTime || !entry.endTime) {
        return t("dashboard:breakIncomplete");
      }
      const start = parseDateTimeInput(startDate, entry.startTime);
      const endDateForEntry =
        startDate !== endDate && entry.endTime < entry.startTime
          ? endDate
          : startDate;
      const end = parseDateTimeInput(endDateForEntry, entry.endTime);
      if (!start || !end || end <= start) {
        return t("dashboard:breakInvalidRange");
      }
      if (start < activityStart || end > activityEnd) {
        return t("dashboard:breakOutsideRange");
      }
    }
    return null;
  };

  const getNetDurationWithBreaks = (
    startDate: string,
    startTime: string,
    endDate: string,
    endTime: string,
    breaks: BreakEntry[]
  ): { baseHours: number; breakHours: number; netHours: number } => {
    const baseHours = calculateDurationHours(
      startDate,
      startTime,
      endDate,
      endTime
    );
    if (baseHours <= 0) {
      return { baseHours: 0, breakHours: 0, netHours: 0 };
    }
    const breakRanges = buildBreakRanges(breaks, startDate, endDate);
    const breakHours = calculateBreakHoursFromRanges(
      breakRanges,
      `${startDate} ${startTime}`,
      `${endDate} ${endTime}`
    );
    const netHours = Math.max(baseHours - breakHours, 0);
    return { baseHours, breakHours, netHours };
  };

  const toBreakEntries = (
    breaks: Array<{ start: string; end: string }>,
    fallbackDate: string
  ): BreakEntry[] => {
    if (!breaks || breaks.length === 0) return [];
    return breaks.map((entry) => {
      const startParts = parseDateTimeValue(entry.start, fallbackDate);
      const endParts = parseDateTimeValue(entry.end, fallbackDate);
      return {
        startTime: startParts.time,
        endTime: endParts.time,
      };
    });
  };

  const getBreakTimeOptions = (): string[] => {
    const times = generateTimeOptions();
    if (!completeModalStartTime || !completeModalEndTime) return times;
    const startIndex = times.indexOf(completeModalStartTime);
    const endIndex = times.indexOf(completeModalEndTime);
    if (startIndex === -1 || endIndex === -1) return times;
    if (startIndex <= endIndex) {
      return times.slice(startIndex, endIndex + 1);
    }
    return [...times.slice(startIndex), ...times.slice(0, endIndex + 1)];
  };

  const resetCompleteModalState = () => {
    const today = new Date().toISOString().split("T")[0];
    completeModalActivityIdRef.current = null;
    setShowCompleteModal(false);
    setActivityToCompleteData(null);
    setCompleteModalStartTime("");
    setCompleteModalEndTime("");
    setCompleteModalStartDate(today);
    setCompleteModalEndDate(today);
    setCompleteModalBreaks([]);
    setCompleteModalFormSubmissions({});
    setCompleteModalSubmitDescription("");
    setCompleteModalFiles([]);
    setCompleteModalDriveAttachments([]);
    setCompleteModalFileDescriptions({});
    setCompleteModalSelectedFolderId(null);
    setCompleteModalSelectedFolderPath(projectFilesRootLabel);
    setCompleteModalSelectedFolderPathEntries(
      buildCompleteModalRootPathEntries(projectFilesRootLabel)
    );
    setShowCompleteFolderSelectModal(false);
    setCompleteModalExistingAttachments([]);
    setCompleteModalRemovedAttachmentIds([]);
    setIsEditMode(false);
    setSelectSearchByField({});
  };

  const addBreakEntry = () => {
    if (
      !completeModalStartDate ||
      !completeModalEndDate ||
      !completeModalStartTime ||
      !completeModalEndTime
    ) {
      return;
    }
    setCompleteModalBreaks((prev) => [
      ...prev,
      {
        startTime: "",
        endTime: "",
      },
    ]);
  };

  const updateBreakEntry = (
    index: number,
    field: keyof BreakEntry,
    value: string
  ) => {
    setCompleteModalBreaks((prev) =>
      prev.map((entry, idx) =>
        idx === index ? { ...entry, [field]: value } : entry
      )
    );
  };

  const removeBreakEntry = (index: number) => {
    setCompleteModalBreaks((prev) => prev.filter((_, idx) => idx !== index));
  };

  const isBreaksReady = Boolean(
    completeModalStartDate &&
      completeModalEndDate &&
      completeModalStartTime &&
      completeModalEndTime
  );

  const completeModalAllowsZeroDuration = allowsZeroDurationForTaskActivity(
    activityToCompleteData
  );
  const completeModalShouldNormalizeZeroDuration =
    shouldNormalizeZeroDurationTimeEntry(
      activityToCompleteData,
      completeModalStartDate,
      completeModalStartTime,
      completeModalEndDate,
      completeModalEndTime,
      completeModalBreaks
    );
  const completeModalHasTimeEntry =
    Boolean(completeModalStartTime) ||
    Boolean(completeModalEndTime) ||
    hasMeaningfulBreakEntry(completeModalBreaks);
  const completeModalHasValidTimeRange =
    completeModalShouldNormalizeZeroDuration ||
    (Boolean(
      completeModalStartDate &&
        completeModalStartTime &&
        completeModalEndDate &&
        completeModalEndTime
    ) &&
      getNetDurationWithBreaks(
        completeModalStartDate,
        completeModalStartTime,
        completeModalEndDate,
        completeModalEndTime,
        completeModalBreaks
      ).netHours > 0);

  const resolveCompleteModalTimePayload = (
    activity: ActivityInstance
  ):
    | {
        duration: number;
        startTime?: string;
        endTime?: string;
        breaks?: Array<{ start: string; end: string }>;
      }
    | null => {
    const allowsZeroDuration = allowsZeroDurationForTaskActivity(activity);
    const shouldNormalizeZeroDuration = shouldNormalizeZeroDurationTimeEntry(
      activity,
      completeModalStartDate,
      completeModalStartTime,
      completeModalEndDate,
      completeModalEndTime,
      completeModalBreaks
    );
    const hasTimeEntry =
      Boolean(completeModalStartTime) ||
      Boolean(completeModalEndTime) ||
      hasMeaningfulBreakEntry(completeModalBreaks);

    if (shouldNormalizeZeroDuration) {
      return {
        duration: 0,
        startTime: `${completeModalStartDate} ${completeModalStartTime}`,
        endTime: `${completeModalEndDate} ${completeModalEndTime}`,
      };
    }

    if (!allowsZeroDuration || hasTimeEntry) {
      if (
        !completeModalStartDate ||
        !completeModalStartTime ||
        !completeModalEndDate ||
        !completeModalEndTime
      ) {
        toast.error(t("dashboard:enterStartEndTime"));
        return null;
      }

      const breakError = getBreakValidationError(
        completeModalBreaks,
        completeModalStartDate,
        completeModalStartTime,
        completeModalEndDate,
        completeModalEndTime
      );
      if (breakError) {
        toast.error(breakError);
        return null;
      }

      const { netHours } = getNetDurationWithBreaks(
        completeModalStartDate,
        completeModalStartTime,
        completeModalEndDate,
        completeModalEndTime,
        completeModalBreaks
      );
      const duration = formatDurationHours(netHours);
      if (duration <= 0) {
        toast.error(t("dashboard:invalidTimeRange"));
        return null;
      }

      return {
        duration,
        startTime: `${completeModalStartDate} ${completeModalStartTime}`,
        endTime: `${completeModalEndDate} ${completeModalEndTime}`,
        breaks: buildBreakRanges(
          completeModalBreaks,
          completeModalStartDate,
          completeModalEndDate
        ),
      };
    }

    return {
      duration: 0,
      ...getAutomaticZeroDurationTimestamp(activity),
    };
  };

  const validateCompleteModalWorkTimeRange = async (
    activity: ActivityInstance,
    timePayload: {
      duration: number;
      startTime?: string;
      endTime?: string;
      breaks?: Array<{ start: string; end: string }>;
    }
  ) => {
    if (!user?.id) {
      return;
    }

    await apiService.validateWorkTimeRange(
      user.id,
      timePayload.startTime,
      timePayload.endTime,
      activity.id
    );
  };

  // Get user's time data (startTime, endTime, duration) from activity
  const getUserTimeData = (
    activity: ActivityInstance,
    userId: number
  ): {
    startTime: string;
    endTime: string;
    duration: number;
    breaks: Array<{ start: string; end: string }>;
  } | null => {
    try {
      if (activity.userDurations) {
        const durations = JSON.parse(activity.userDurations);
        const userData = durations[userId.toString()];
        if (userData && typeof userData === "object") {
          const breaks = Array.isArray(userData.breaks)
            ? userData.breaks
            : [];
          return {
            startTime: userData.startTime || "",
            endTime: userData.endTime || "",
            duration: userData.duration || 0,
            breaks,
          };
        }
        // Legacy support: if duration is just a number
        if (typeof userData === "number") {
          return { startTime: "", endTime: "", duration: userData, breaks: [] };
        }
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const getUserDuration = (
    activity: ActivityInstance,
    userId: number
  ): number => {
    try {
      if (activity.userDurations) {
        const durations = JSON.parse(activity.userDurations);
        const userData = durations[userId.toString()];
        // New format: { startTime, endTime, duration }
        if (userData && typeof userData === "object") {
          return userData.duration || 0;
        }
        // Legacy format: just a number
        if (typeof userData === "number") {
          return userData;
        }
      }
      return 0;
    } catch (error) {
      return 0;
    }
  };

  const getSubmitDescriptionData = (submitDescription?: string) => {
    if (!submitDescription) {
      return { entries: [] as any[], userDescription: "" };
    }

    try {
      const parsed = JSON.parse(submitDescription);
      if (Array.isArray(parsed)) {
        const userEntry = parsed.find(
          (entry) => entry?.userId?.toString() === user?.id?.toString()
        );
        return {
          entries: parsed,
          userDescription: userEntry?.description || "",
        };
      }
    } catch {
      // fall through to legacy handling
    }

    const legacyEntry = {
      description: submitDescription,
      timestamp: "",
      userName: t("common:previousUser") || "Previous User",
    };
    return { entries: [legacyEntry], userDescription: submitDescription };
  };

  const getUserFormDataForActivity = (
    activity: ActivityInstance,
    userId: number
  ): Record<string, any> => {
    if (!activity.userFormData) return {};
    try {
      const allUserFormData = JSON.parse(activity.userFormData);
      const userFormData = allUserFormData?.[userId.toString()];
      return userFormData && typeof userFormData === "object"
        ? userFormData
        : {};
    } catch (error) {
      console.warn("Failed to parse activity userFormData:", error);
      return {};
    }
  };

  const getExistingFormResponse = (
    activity: ActivityInstance,
    form: NonNullable<ActivityInstance["forms"]>[number]
  ): any => {
    if (form.userResponse && Object.keys(form.userResponse).length > 0) {
      return form.userResponse;
    }

    if (!user?.id) return null;
    const userFormData = getUserFormDataForActivity(activity, user.id);
    return (
      userFormData[String(form.id)] ||
      (form.templateFormId ? userFormData[String(form.templateFormId)] : null) ||
      null
    );
  };

  const handleCompleteActivity = (activityInstanceId: number) => {
    // Find the activity
    const activity = activityInstances.find((a) => a.id === activityInstanceId);

    if (!activity) {
      toast.error(t("dashboard:noActivitiesFound"));
      return;
    }

    setIsEditMode(false);
    setCompleteModalExistingAttachments([]);
    setCompleteModalRemovedAttachmentIds([]);

    // Open unified complete modal
    setActivityToCompleteData(activity);

    // Load existing time data if available
    const userTimeData = getUserTimeData(activity, user!.id);
    const fallbackStartDate = formatLocalDatePart(new Date());
    const fallbackEndDate = fallbackStartDate;
    const startParts = parseDateTimeValue(
      userTimeData?.startTime,
      fallbackStartDate
    );
    const endParts = parseDateTimeValue(userTimeData?.endTime, fallbackEndDate);
    setCompleteModalStartDate(startParts.date || fallbackStartDate);
    setCompleteModalStartTime(startParts.time);
    setCompleteModalEndDate(endParts.date || fallbackEndDate);
    setCompleteModalEndTime(endParts.time);
    setCompleteModalBreaks(
      userTimeData?.breaks
        ? toBreakEntries(userTimeData.breaks, fallbackStartDate)
        : []
    );

    // Load existing form submissions
    if (activity.forms && activity.forms.length > 0) {
      const existingFormData: Record<number, any> = {};
      activity.forms.forEach((form) => {
        const existingResponse = getExistingFormResponse(activity, form);
        if (existingResponse && Object.keys(existingResponse).length > 0) {
          existingFormData[form.id] = existingResponse;
        }
      });
      setCompleteModalFormSubmissions(existingFormData);
    } else {
      setCompleteModalFormSubmissions({});
    }

    const submitData = getSubmitDescriptionData(activity.submitDescription);
    setExistingSubmitDescriptions(submitData.entries);
    setCompleteModalSubmitDescription("");

    syncCompleteModalRootFolderLabel(activity);
    setShowCompleteFolderSelectModal(false);

    setShowCompleteModal(true);
  };

  const handleEditHistoryActivity = (activity: ActivityInstance) => {
    setIsEditMode(true);
    setCompleteModalRemovedAttachmentIds([]);
    setCompleteModalExistingAttachments(getAttachments(activity));
    setActivityToCompleteData(activity);

    const userTimeData = getUserTimeData(activity, user!.id);
    const fallbackStartDate = formatLocalDatePart(new Date());
    const fallbackEndDate = fallbackStartDate;
    const startParts = parseDateTimeValue(
      userTimeData?.startTime,
      fallbackStartDate
    );
    const endParts = parseDateTimeValue(userTimeData?.endTime, fallbackEndDate);
    setCompleteModalStartDate(startParts.date || fallbackStartDate);
    setCompleteModalStartTime(startParts.time);
    setCompleteModalEndDate(endParts.date || fallbackEndDate);
    setCompleteModalEndTime(endParts.time);
    setCompleteModalBreaks(
      userTimeData?.breaks
        ? toBreakEntries(userTimeData.breaks, fallbackStartDate)
        : []
    );

    if (activity.forms && activity.forms.length > 0) {
      const existingFormData: Record<number, any> = {};
      activity.forms.forEach((form) => {
        const existingResponse = getExistingFormResponse(activity, form);
        if (existingResponse && Object.keys(existingResponse).length > 0) {
          existingFormData[form.id] = existingResponse;
        }
      });
      setCompleteModalFormSubmissions(existingFormData);
    } else {
      setCompleteModalFormSubmissions({});
    }

    const submitData = getSubmitDescriptionData(activity.submitDescription);
    setExistingSubmitDescriptions(submitData.entries);
    setCompleteModalSubmitDescription(submitData.userDescription);

    setCompleteModalFiles([]);
    setCompleteModalDriveAttachments([]);
    setCompleteModalFileDescriptions({});
    syncCompleteModalRootFolderLabel(activity);
    setShowCompleteFolderSelectModal(false);
    setShowCompleteModal(true);
  };

  const canEditHistoryActivity = (activity: ActivityInstance): boolean => {
    if (canBypassActivityEditWindow) {
      return true;
    }

    if (!activity.completedAt) {
      return true;
    }

    const completedAt = new Date(activity.completedAt);
    if (Number.isNaN(completedAt.getTime())) {
      return true;
    }

    return Date.now() <= completedAt.getTime() + 24 * 60 * 60 * 1000;
  };

  const handleRemoveExistingAttachment = (attachmentId: string) => {
    setCompleteModalRemovedAttachmentIds((prev) =>
      prev.includes(attachmentId) ? prev : [...prev, attachmentId]
    );
    setCompleteModalExistingAttachments((prev) =>
      prev.filter((attachment) => attachment.id !== attachmentId)
    );
  };

  const showCompletionToast = (status?: string) => {
    if (status === "PENDING_APPROVAL") {
      toast.success(t("dashboard:activitySubmittedForApproval"));
      return;
    }
    toast.success(t("dashboard:activityCompleted"));
  };

  const applyDailyHoursUpdate = (status?: DailyHoursStatus | null) => {
    if (
      !status ||
      status.isWorkday === false ||
      typeof status.dailyRemainingHours !== "number"
    ) {
      return;
    }
    setDailyHoursStatus(status);
    if (status.dailyOverHours && status.dailyOverHours > 0) {
      toast.success(
        t("dashboard:dailyHoursOver", {
          hours: status.dailyOverHours.toFixed(2),
        })
      );
      return;
    }
    toast.success(
      t("dashboard:dailyHoursRemaining", {
        hours: status.dailyRemainingHours.toFixed(2),
      })
    );
  };

  const confirmCompleteActivity = async () => {
    if (!activityToComplete) return;

    try {
      const result = await apiService.completeActivityInstance(
        activityToComplete.id
      );
      showCompletionToast(result?.status);
      setShowCompleteConfirm(false);
      setActivityToComplete(null);
      await loadDashboardData();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || t("dashboard:activityCompleteFailed")
      );
    }
  };

  const handleCompleteModalSubmit = async () => {
    if (!activityToCompleteData) return;
    const timePayload = resolveCompleteModalTimePayload(activityToCompleteData);
    if (!timePayload) return;

    // Validation 2: Forms must be submitted if they exist
    if (
      activityToCompleteData.forms &&
      activityToCompleteData.forms.length > 0
    ) {
      const hasUnsubmittedForms = activityToCompleteData.forms.some((form) => {
        const formData =
          completeModalFormSubmissions[form.id] ||
          getExistingFormResponse(activityToCompleteData, form) ||
          {};
        return !formData || Object.keys(formData).length === 0;
      });

      if (hasUnsubmittedForms) {
        toast.error(t("dashboard:fillAllForms"));
        return;
      }
    }

    try {
      await validateCompleteModalWorkTimeRange(
        activityToCompleteData,
        timePayload
      );

      const uploadedAttachments: GoogleDriveAttachment[] = [];
      const copiedDriveAttachments: GoogleDriveAttachment[] = [];

      if (
        completeModalFiles.length > 0 ||
        completeModalDriveAttachments.length > 0
      ) {
        setUploadingFiles(true);
        try {
          if (completeModalFiles.length > 0) {
            const folderCache = new Map<string, string | null>();
            const rootFolderNameCache = new Map<string, string>();
            for (const selectedFile of completeModalFiles) {
              const targetFolderId =
                await resolveUploadFolderIdForCompleteModalFile(
                  activityToCompleteData.workflowInstanceId,
                  selectedFile,
                  folderCache,
                  rootFolderNameCache
                );
              const attachment = await apiService.uploadFileToProject(
                activityToCompleteData.workflowInstanceId,
                selectedFile.file,
                completeModalFileDescriptions[selectedFile.id],
                undefined,
                undefined,
                targetFolderId
              );
              uploadedAttachments.push(attachment);
            }
          }

          if (completeModalDriveAttachments.length > 0) {
            for (const attachment of completeModalDriveAttachments) {
              const copiedAttachment = await apiService.copyDriveFileToProject(
                activityToCompleteData.workflowInstanceId,
                attachment.id,
                completeModalFileDescriptions[attachment.id] ||
                  attachment.description,
                attachment.targetFolderId
              );
              copiedDriveAttachments.push(copiedAttachment);
            }
          }

          if (
            completeModalFiles.length > 0 ||
            completeModalDriveAttachments.length > 0
          ) {
            toast.success(t("dashboard:filesUploaded"));
          }
        } catch (uploadError: any) {
          toast.error(t("dashboard:fileUploadFailed"));
          setUploadingFiles(false);
          return;
        }
        setUploadingFiles(false);
      }

      const driveFilesWithDescriptions = copiedDriveAttachments.map(
        (att) => ({
          ...att,
          description: completeModalFileDescriptions[att.id] || att.description,
          uploadedAt: new Date().toISOString(),
          uploadedBy: user?.id,
          uploadedByName: user
            ? `${user.firstName} ${user.lastName}`
            : undefined,
        })
      );

      const allAttachments = [
        ...uploadedAttachments,
        ...driveFilesWithDescriptions,
      ].map((att) => ({
        ...att,
        addedBy: user?.id || 0,
        addedAt: att.addedAt || new Date().toISOString(),
      }));

      // Save duration and forms together
      let formDataToSubmit: Record<string, any> = {};

      // Collect all form submissions
      if (
        activityToCompleteData.forms &&
        activityToCompleteData.forms.length > 0
      ) {
        activityToCompleteData.forms.forEach((form) => {
          const submissionData =
            completeModalFormSubmissions[form.id] ||
            getExistingFormResponse(activityToCompleteData, form) ||
            {};
          if (submissionData && Object.keys(submissionData).length > 0) {
            formDataToSubmit[form.id] = submissionData;
          }
        });
      }

      // Save time data (startTime, endTime, duration) along with forms
      // Combine date and time
      const submitResponse = await apiService.submitUserSpecificFormData(
        activityToCompleteData.id,
        formDataToSubmit,
        timePayload.duration,
        timePayload.startTime,
        timePayload.endTime,
        timePayload.breaks
      );
      applyDailyHoursUpdate(submitResponse);

      // Complete the activity
      const result = await apiService.completeActivityInstance(
        activityToCompleteData.id,
        completeModalSubmitDescription,
        allAttachments
      );

      showCompletionToast(result?.status);
      resetCompleteModalState();
      await Promise.all([loadDashboardData(), loadActivityHistory()]);
    } catch (error: any) {
      const backendMessage = error?.response?.data?.message;
      if (
        backendMessage ===
        "Work time overlaps with another activity on the same day."
      ) {
        toast.error(t("dashboard:workTimeOverlap"));
      } else {
        toast.error(backendMessage || t("dashboard:activityCompleteFailed"));
      }
    }
  };

  const handleEditModalSubmit = async () => {
    if (!activityToCompleteData) return;
    const timePayload = resolveCompleteModalTimePayload(activityToCompleteData);
    if (!timePayload) return;

    let formDataToSubmit: Record<string, any> = {};
    if (
      activityToCompleteData.forms &&
      activityToCompleteData.forms.length > 0
    ) {
      activityToCompleteData.forms.forEach((form) => {
        const submissionData =
          completeModalFormSubmissions[form.id] ||
          getExistingFormResponse(activityToCompleteData, form) ||
          {};
        if (submissionData && Object.keys(submissionData).length > 0) {
          formDataToSubmit[form.id] = submissionData;
        }
      });
    }

    try {
      await validateCompleteModalWorkTimeRange(
        activityToCompleteData,
        timePayload
      );

      const uploadedAttachments: GoogleDriveAttachment[] = [];
      const copiedDriveAttachments: GoogleDriveAttachment[] = [];

      if (
        completeModalFiles.length > 0 ||
        completeModalDriveAttachments.length > 0
      ) {
        setUploadingFiles(true);
        try {
          if (completeModalFiles.length > 0) {
            const folderCache = new Map<string, string | null>();
            const rootFolderNameCache = new Map<string, string>();
            for (const selectedFile of completeModalFiles) {
              const targetFolderId =
                await resolveUploadFolderIdForCompleteModalFile(
                  activityToCompleteData.workflowInstanceId,
                  selectedFile,
                  folderCache,
                  rootFolderNameCache
                );
              const attachment = await apiService.uploadFileToProject(
                activityToCompleteData.workflowInstanceId,
                selectedFile.file,
                completeModalFileDescriptions[selectedFile.id],
                undefined,
                undefined,
                targetFolderId
              );
              uploadedAttachments.push(attachment);
            }
          }

          if (completeModalDriveAttachments.length > 0) {
            for (const attachment of completeModalDriveAttachments) {
              const copiedAttachment = await apiService.copyDriveFileToProject(
                activityToCompleteData.workflowInstanceId,
                attachment.id,
                completeModalFileDescriptions[attachment.id] ||
                  attachment.description,
                attachment.targetFolderId
              );
              copiedDriveAttachments.push(copiedAttachment);
            }
          }

          if (
            completeModalFiles.length > 0 ||
            completeModalDriveAttachments.length > 0
          ) {
            toast.success(t("dashboard:filesUploaded"));
          }
        } catch (uploadError: any) {
          toast.error(t("dashboard:fileUploadFailed"));
          setUploadingFiles(false);
          return;
        }
        setUploadingFiles(false);
      }

      const driveFilesWithDescriptions = copiedDriveAttachments.map(
        (att) => ({
          ...att,
          description: completeModalFileDescriptions[att.id] || att.description,
          uploadedAt: new Date().toISOString(),
          uploadedBy: user?.id,
          uploadedByName: user
            ? `${user.firstName} ${user.lastName}`
            : undefined,
        })
      );

      const allAttachments = [
        ...uploadedAttachments,
        ...driveFilesWithDescriptions,
      ].map((att) => ({
        ...att,
        addedBy: user?.id || 0,
        addedAt: att.addedAt || new Date().toISOString(),
      }));

      const updateResponse = await apiService.updateActivityHistory(
        activityToCompleteData.id,
        {
        formData: formDataToSubmit,
        duration: timePayload.duration,
        startTime: timePayload.startTime,
        endTime: timePayload.endTime,
        breaks: timePayload.breaks,
        submitDescription: completeModalSubmitDescription,
        attachments: allAttachments.length > 0 ? allAttachments : undefined,
        removeAttachmentIds: completeModalRemovedAttachmentIds,
        }
      );
      applyDailyHoursUpdate(updateResponse);

      toast.success(t("activities:activityUpdated"));
      resetCompleteModalState();
      await Promise.all([loadDashboardData(), loadActivityHistory()]);
    } catch (error: any) {
      const backendMessage = error?.response?.data?.message;
      if (
        backendMessage ===
        "Work time overlaps with another activity on the same day."
      ) {
        toast.error(t("dashboard:workTimeOverlap"));
      } else {
        toast.error(backendMessage || t("errors:updateFailed"));
      }
    } finally {
      setUploadingFiles(false);
    }
  };

  const updateCompleteModalFormSubmission = (
    formInstanceId: number,
    field: string,
    value: any
  ) => {
    setCompleteModalFormSubmissions((prev) => ({
      ...prev,
      [formInstanceId]: {
        ...(prev[formInstanceId] || {}),
        [field]: value,
      },
    }));
  };

  const normalizeToStringArray = (value: any): string[] => {
    if (Array.isArray(value)) {
      return value
        .filter((item) => item !== undefined && item !== null && item !== "")
        .map((item) => String(item));
    }
    if (value === undefined || value === null || value === "") {
      return [];
    }
    return [String(value)];
  };

  const normalizeSelectSubmissionValue = (values: string[]): string | string[] => {
    const uniqueValues = Array.from(new Set(values));
    if (uniqueValues.length <= 1) {
      return uniqueValues[0] || "";
    }
    return uniqueValues;
  };

  const getSelectSearchKey = (
    scope: "active" | "complete",
    activityId: number | null | undefined,
    formId: number,
    elementId: string,
  ): string => `${scope}:${activityId ?? "na"}:${formId}:${elementId}`;

  const cancelCompleteActivity = () => {
    setShowCompleteConfirm(false);
    setActivityToComplete(null);
  };

  const handleOpenDeleteConfirm = (activity: ActivityInstance) => {
    setActivityToDelete(activity);
    setShowDeleteConfirm(true);
  };

  const handleCancelDeleteActivity = () => {
    setShowDeleteConfirm(false);
    setActivityToDelete(null);
  };

  const handleConfirmDeleteActivity = async () => {
    if (!activityToDelete) return;

    try {
      await apiService.deleteActivityInstance(activityToDelete.id);
      toast.success(t("activities:activityDeletedSuccessfully"));
      setShowDeleteConfirm(false);
      setActivityToDelete(null);
      await Promise.all([loadDashboardData(), loadActivityHistory()]);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t("errors:deleteFailed"));
    }
  };

  const handleSubmitForm = async (formInstanceId: number) => {
    try {
      if (!selectedActivityForms) return;

      const submissionData = formSubmissions[formInstanceId] || {};


      // Get current user's duration (if already set)
      const currentDuration =
        getUserDuration(selectedActivityForms, user!.id) || 0;

      // ✅ CRITICAL FIX: Preserve existing form data for OTHER forms
      // Parse existing userFormData to get this user's ALL form submissions
      let existingFormData: Record<string, any> = {};

      if (selectedActivityForms.userFormData) {
        try {
          const allUserFormData = JSON.parse(
            selectedActivityForms.userFormData
          );
          existingFormData = allUserFormData[user!.id.toString()] || {};
        } catch (e) {
        }
      }

      // Merge: Keep existing forms + add/update this form
      const formDataToSubmit = {
        ...existingFormData, // ✅ Keep all existing form submissions
        [formInstanceId]: submissionData, // Update/add this form
      };

      // Save to ActivityInstance.userFormData (user-specific)
      // This allows multiple users to submit their own form responses
      const submitResponse = await apiService.submitUserSpecificFormData(
        selectedActivityForms.id, // activityInstanceId
        formDataToSubmit, // formData keyed by formInstanceId
        currentDuration // preserve existing duration
      );
      applyDailyHoursUpdate(submitResponse);

      toast.success(t("dashboard:formSubmitted"));
      await loadDashboardData();
      setSelectedActivityForms(null);
      setFormSubmissions({});
      setSelectSearchByField({});
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || t("dashboard:formSubmitFailed")
      );
    }
  };

  const handleSubmitUserSpecificForm = async (
    activityInstanceId: number,
    formData: any,
    duration: number
  ) => {
    try {
      const submitResponse = await apiService.submitUserSpecificFormData(
        activityInstanceId,
        formData,
        duration
      );
      applyDailyHoursUpdate(submitResponse);
      toast.success(t("dashboard:formSubmitted"));
      await loadDashboardData();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || t("dashboard:formSubmitFailed")
      );
    }
  };

  const handleOpenDurationModal = (activity: ActivityInstance) => {
    setSelectedActivityForDuration(activity);
    const userDuration = getUserDuration(activity, user!.id);
    setDurationInput(userDuration > 0 ? userDuration.toString() : "");
    setDurationError(""); // Reset error message
    setShowDurationModal(true);
  };

  const handleSaveDuration = async () => {
    if (!selectedActivityForDuration) return;

    const duration = parseFloat(durationInput);
    if (isNaN(duration) || duration <= 0) {
      toast.error(t("dashboard:invalidDuration"));
      return;
    }

    try {
      // ✅ CRITICAL FIX: Preserve existing form data when saving duration
      // Parse existing userFormData to get this user's current form submissions
      let existingFormData: Record<string, any> = {};

      if (selectedActivityForDuration.userFormData) {
        try {
          const allUserFormData = JSON.parse(
            selectedActivityForDuration.userFormData
          );
          existingFormData = allUserFormData[user!.id.toString()] || {};
        } catch (e) {
        }
      }

      // Save user-specific duration (preserve existing formData!)
      const submitResponse = await apiService.submitUserSpecificFormData(
        selectedActivityForDuration.id,
        existingFormData, // ✅ Keep existing form submissions!
        duration
      );
      applyDailyHoursUpdate(submitResponse);
      toast.success(t("dashboard:durationSaved"));
      setShowDurationModal(false);
      setSelectedActivityForDuration(null);
      setDurationInput("");
      setDurationError("");
      await loadDashboardData();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || t("dashboard:durationSaveFailed")
      );
    }
  };

  const updateFormSubmission = (
    formInstanceId: number,
    field: string,
    value: any
  ) => {
    setFormSubmissions((prev) => ({
      ...prev,
      [formInstanceId]: {
        ...(prev[formInstanceId] || {}),
        [field]: value,
      },
    }));
  };

  if (user?.role === UserRole.VISITOR) {
    return <Navigate to="/project-instances" replace />;
  }

  if (loading) {
    return (
      <div className="app-page flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">{t("common:loading")}</p>
        </div>
      </div>
    );
  }

  const filteredActivities = getFilteredActivities();
  const filteredHistoryActivities = getFilteredHistoryActivities();
  const activeActivitiesTotalPages = Math.max(
    1,
    Math.ceil(filteredActivities.length / ACTIVE_ACTIVITIES_PAGE_SIZE)
  );
  const historyActivitiesTotalPages = Math.max(
    1,
    Math.ceil(filteredHistoryActivities.length / DASHBOARD_HISTORY_PAGE_SIZE)
  );
  const paginatedActiveActivities = filteredActivities.slice(
    (activeActivitiesPage - 1) * ACTIVE_ACTIVITIES_PAGE_SIZE,
    activeActivitiesPage * ACTIVE_ACTIVITIES_PAGE_SIZE
  );
  const paginatedHistoryActivities = filteredHistoryActivities.slice(
    (historyActivitiesPage - 1) * DASHBOARD_HISTORY_PAGE_SIZE,
    historyActivitiesPage * DASHBOARD_HISTORY_PAGE_SIZE
  );

  // Always show modern full-screen dashboard
  return (
    <div className="app-page bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="flex flex-col items-center gap-4">
          {dailyHoursStatus?.dailyRemainingHours !== undefined &&
            dailyHoursStatus?.isWorkday !== false && (
            <div
              className={`inline-flex items-center gap-3 px-4 py-2 rounded-full border font-semibold text-sm shadow-sm ${
                dailyHoursStatus.dailyOverHours &&
                dailyHoursStatus.dailyOverHours > 0
                  ? "bg-red-50 border-red-200 text-red-700"
                  : "bg-white/80 border-purple-200 text-purple-700"
              }`}
            >
              <ClockIcon className="w-4 h-4" />
              <span>
                {dailyHoursStatus.dailyOverHours &&
                dailyHoursStatus.dailyOverHours > 0
                  ? t("dashboard:dailyHoursOverHeader", {
                      hours: dailyHoursStatus.dailyOverHours.toFixed(2),
                    })
                  : t("dashboard:dailyHoursHeader", {
                      hours: dailyHoursStatus.dailyRemainingHours.toFixed(2),
                    })}
              </span>
            </div>
          )}

            <div className="flex justify-center gap-4 flex-wrap">
            <button
              onClick={() => setShowAddActivityModal(true)}
              className="group relative flex items-center px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 font-semibold text-base"
            >
              <div className="absolute inset-0 bg-white/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <PlusIcon className="h-6 w-6 mr-2 relative z-10" />
              <span className="relative z-10">
                {t("dashboard:addActivity")}
              </span>
            </button>

            <button
              onClick={() => setShowUploadFilesModal(true)}
              className="group relative flex items-center px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 font-semibold text-base"
            >
              <div className="absolute inset-0 bg-white/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <PaperClipIcon className="h-6 w-6 mr-2 relative z-10" />
              <span className="relative z-10">
                {t("dashboard:uploadProjectFiles.button")}
              </span>
            </button>
          </div>
          </div>
        </div>

        {/* Active Activities Section - Green Background - Only show if there are activities */}
        {activityInstances.length > 0 && (
          <div className="bg-green-50/50 backdrop-blur-sm rounded-xl border border-green-200/50 shadow-sm p-6">
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <PlayIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <h2 className="text-lg font-bold text-gray-900">
                        {t("dashboard:activeActivities")}
                      </h2>
                      <span className="text-xs font-semibold text-gray-600 bg-white/80 border border-gray-200 rounded-full px-2.5 py-1">
                        {t("dashboard:activityInstanceCount", {
                          count: activityInstances.length,
                        })}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      {t("dashboard:activeActivitiesDesc")}
                    </p>
                  </div>
                </div>

                {/* Filter and Search */}
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Filter: Project Selection */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                      {t("dashboard:filterByProject")}:
                    </label>
                    <select
                      value={activeFilter}
                      onChange={(e) => setActiveFilter(e.target.value)}
                      className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="all">
                        {t("activities:allActivities")}
                      </option>
                      {getUniqueActiveProjects().map((projectName) => (
                        <option key={projectName} value={projectName}>
                          {projectName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Search - Active Activities */}
                  <div className="relative w-full sm:w-auto sm:min-w-[200px]">
                    <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder={t("dashboard:searchActivities")}
                      value={activeSearchTerm}
                      onChange={(e) => setActiveSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white shadow-sm transition-all"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Activities List */}
            {filteredActivities.length === 0 ? (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-2xl mb-6">
                  <CubeIcon className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">
                  {t("dashboard:noActivitiesFound")}
                </h3>
                <p className="text-xs text-gray-500">
                  {activeSearchTerm
                    ? t("common:noResults")
                    : t("dashboard:noActivitiesAssigned")}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {paginatedActiveActivities.map((activity, index) => {
                  const gradientClass = getActiveActivityGradient();
                  const activityDescription = (
                    activity.description ||
                    activity.activityDescription ||
                    ""
                  ).trim();
                  const showDescriptionInfoButton =
                    activityDescription.length > 0;
                  const progress = getCompletionProgress(activity);
                  const showProgress =
                    progress.total > 0 && activity.status === "ACTIVE";
                  const showAssignedUsers =
                    Boolean(activity.assignedUsersList) &&
                    activity.assignedUsersList!.length > 1;
                  const isManualAssignedTask =
                    isManualAssignedTaskActivity(activity);
                  const attachments = getAttachments(activity);
                  const hasForms =
                    Boolean(activity.forms) && activity.forms!.length > 0;
                  const hasAttachments = attachments.length > 0;

                  return (
                    <div
                      key={activity.id}
                      className="bg-white/70 backdrop-blur-sm rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
                    >
                      <div className="flex items-stretch">
                        {/* Color Strip */}
                        <div
                          className={`w-1.5 bg-gradient-to-b ${gradientClass} flex-shrink-0`}
                        ></div>

                        {/* Main Content */}
                        <div className="flex-1 flex flex-wrap items-center px-4 lg:px-5 py-3 gap-4">
                          {/* Left Section: Icon, Title, Status */}
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            {activity.icon && (
                              <div
                                className={`w-10 h-10 bg-gradient-to-br ${gradientClass} rounded-lg flex items-center justify-center flex-shrink-0`}
                              >
                                <span className="text-xl">{activity.icon}</span>
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <h3 className="text-sm font-semibold text-gray-900 truncate">
                                  {activity.projectName
                                    ? `${activity.name} - ${activity.projectName}`
                                    : activity.name}
                                </h3>
                                {isManualAssignedTask && (
                                  <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                    {t("dashboard:task")}
                                  </span>
                                )}
                                {activity.status !== "ACTIVE" && (
                                  <span className="flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                                    {getActivityStatusLabel(activity.status)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Middle Section: Details */}
                          <div className="hidden lg:flex flex-col gap-1.5 min-w-0 flex-1">
                            <div className="flex items-center gap-3 text-xs text-gray-600 min-w-0">
                              {(activity.startDate || activity.endDate) && (
                                <span className="flex items-center gap-1">
                                  <CalendarDaysIcon className="w-3.5 h-3.5" />
                                  {activity.startDate &&
                                    new Date(
                                      activity.startDate
                                    ).toLocaleDateString("tr-TR")}
                                  {activity.startDate &&
                                    activity.endDate &&
                                    " - "}
                                  {activity.endDate &&
                                    new Date(
                                      activity.endDate
                                    ).toLocaleDateString("tr-TR")}
                                </span>
                              )}
                              {activityDescription && (
                                <div className="flex items-center gap-1.5 min-w-0 max-w-[240px] xl:max-w-[420px]">
                                  <span
                                    className="truncate block"
                                    title={activityDescription}
                                  >
                                    {activityDescription}
                                  </span>
                                  {showDescriptionInfoButton && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setDescriptionInfoModal({
                                          title: activity.projectName
                                            ? `${activity.name} - ${activity.projectName}`
                                            : activity.name,
                                          description: activityDescription,
                                        })
                                      }
                                      className="flex-shrink-0 text-slate-400 hover:text-blue-600 transition-colors"
                                      title="Tam açıklamayı görüntüle"
                                    >
                                      <InformationCircleIcon className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                            {activity.tags && (
                              <div className="flex items-center gap-1 flex-wrap">
                                {activity.tags
                                  .split(",")
                                  .slice(0, 2)
                                  .map((tag, tagIndex) => (
                                    <span
                                      key={tagIndex}
                                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
                                    >
                                      {tag.trim()}
                                    </span>
                                  ))}
                                {activity.tags.split(",").length > 2 && (
                                  <span className="text-xs text-gray-400">
                                    +{activity.tags.split(",").length - 2}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Progress Column (fixed width for alignment) */}
                          <div className="hidden xl:flex w-[140px] flex-shrink-0">
                            {showProgress && (
                              <div className="flex w-full flex-col gap-1">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-medium text-blue-700">
                                    {t("dashboard:progress")}
                                  </span>
                                  <span className="text-xs font-bold text-blue-600">
                                    {progress.completed}/{progress.total}
                                  </span>
                                </div>
                                <div className="w-full bg-blue-200 rounded-full h-1.5">
                                  <div
                                    className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                                    style={{
                                      width: `${progress.percentage}%`,
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Meta Column (fixed width for alignment) */}
                          <div className="hidden xl:flex w-[220px] flex-shrink-0 items-center">
                            <div className="flex items-center gap-3 text-xs text-gray-600 whitespace-nowrap">
                              {showAssignedUsers && activity.assignedUsersList && (
                                <div className="flex -space-x-1.5">
                                  {activity.assignedUsersList
                                    .slice(0, 3)
                                    .map((user) => (
                                      <div
                                        key={user.id}
                                        className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-medium border-2 border-white"
                                        title={`${user.firstName} ${user.lastName}`}
                                      >
                                        {user.firstName[0]}
                                        {user.lastName[0]}
                                      </div>
                                    ))}
                                  {activity.assignedUsersList.length > 3 && (
                                    <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-xs font-medium border-2 border-white">
                                      +{activity.assignedUsersList.length - 3}
                                    </div>
                                  )}
                                </div>
                              )}
                              {hasForms && activity.forms && (
                                <span className="flex items-center gap-1">
                                  <DocumentTextIcon className="w-4 h-4 text-blue-500" />
                                  <span>
                                    {t("dashboard:formCount", {
                                      count: activity.forms.length,
                                    })}
                                  </span>
                                </span>
                              )}
                              {hasAttachments && (
                                <span className="flex items-center gap-1">
                                  <DocumentTextIcon className="w-4 h-4 text-green-500" />
                                  <span>
                                    {t("dashboard:fileCount", {
                                      count: attachments.length,
                                    })}
                                  </span>
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Actions Column (fixed width for alignment) */}
                          <div className="flex w-[170px] flex-shrink-0 items-center justify-end ml-auto">
                            {activity.status === "PENDING" && (
                              <div className="flex items-center gap-1 px-3 py-1.5 text-[11px] bg-gray-100 text-gray-400 rounded-lg cursor-not-allowed whitespace-nowrap">
                                <ClockIcon className="w-4 h-4" />
                                <span>{t("dashboard:pending")}</span>
                              </div>
                            )}
                            {activity.status === "ACTIVE" &&
                              (() => {
                                // Check if current user has already completed this activity
                                const completedUsers =
                                  activity.completedUserIds
                                    ? JSON.parse(activity.completedUserIds)
                                    : [];
                                const hasCompleted = completedUsers.includes(
                                  user!.id
                                );

                                if (hasCompleted) {
                                  // User has completed, waiting for others
                                  return (
                                    <div className="flex items-center gap-1 px-3 py-1.5 text-[11px] bg-blue-100 text-blue-600 rounded-lg whitespace-nowrap">
                                      <ClockIcon className="w-4 h-4" />
                                      <span>{t("dashboard:waitingForOthers")}</span>
                                    </div>
                                  );
                                }

                                // User hasn't completed yet
                                return (
                                  <button
                                    onClick={() =>
                                      handleCompleteActivity(activity.id)
                                    }
                                    className="flex items-center gap-1 px-3 py-1.5 text-[11px] bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
                                  >
                                    <CheckIcon className="w-4 h-4" />
                                    <span>{t("dashboard:complete")}</span>
                                  </button>
                                );
                              })()}
                            {activity.status === "REJECTED" && (
                              <button
                                onClick={() =>
                                  handleCompleteActivity(activity.id)
                                }
                                className="flex items-center gap-1 px-3 py-1.5 text-[11px] bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
                              >
                                <ArrowPathIcon className="w-4 h-4" />
                                <span>{t("dashboard:resubmit")}</span>
                              </button>
                            )}
                            {activity.status === "COMPLETED" && (
                              <div className="flex items-center gap-1 px-3 py-1.5 text-[11px] bg-green-100 text-green-600 rounded-lg whitespace-nowrap">
                                <CheckIcon className="w-4 h-4" />
                                <span>{t("dashboard:completed")}</span>
                              </div>
                            )}
                            <button
                              onClick={() => handleViewDetails(activity)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                              title={t("common:viewDetails")}
                            >
                              <EyeIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Expandable Details (for mobile/tablet) */}
                      {(activityDescription ||
                        activity.startedAt ||
                        activity.completedAt) && (
                        <div className="md:hidden border-t border-gray-100 px-4 lg:px-5 py-2 bg-gray-50 space-y-2">
                          {activityDescription && (
                            <p className="text-[11px] text-gray-600">
                              {activityDescription}
                            </p>
                          )}
                          {(activity.startedAt || activity.completedAt) && (
                            <div className="space-y-1 text-[11px] text-gray-500">
                              {activity.startedAt && (
                                <div className="flex items-center gap-1.5">
                                  <PlayIcon className="w-3.5 h-3.5 text-blue-500" />
                                  Started:{" "}
                                  {new Date(activity.startedAt).toLocaleString(
                                    "tr-TR"
                                  )}
                                </div>
                              )}
                              {activity.completedAt && (
                                <div className="flex items-center gap-1.5">
                                  <CheckIcon className="w-3.5 h-3.5 text-green-500" />
                                  Completed:{" "}
                                  {new Date(
                                    activity.completedAt
                                  ).toLocaleString("tr-TR")}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                <PaginationControls
                  currentPage={activeActivitiesPage}
                  totalPages={activeActivitiesTotalPages}
                  totalItems={filteredActivities.length}
                  itemsPerPage={ACTIVE_ACTIVITIES_PAGE_SIZE}
                  onPageChange={setActiveActivitiesPage}
                  accent="green"
                />
              </div>
            )}
          </div>
        )}

        {/* Manager Comments Notifications */}
        {!isAdmin() && !isManager() && (
          <div className="mt-8 bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center border border-emerald-100">
                  <DocumentTextIcon className="w-5 h-5 text-emerald-700" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-base font-bold text-gray-900 truncate">
                    {t("dashboard:managerCommentsTitle")}
                  </h2>
                  <p className="text-xs text-gray-600">
                    {t("dashboard:managerCommentsDesc")}
                  </p>
                </div>
              </div>
              <div className="text-xs font-semibold text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                {loadingManagerComments
                  ? t("dashboard:loading")
                  : t("dashboard:managerCommentsCount", {
                      count: unreadManagerComments.length,
                    })}
              </div>
            </div>

            {loadingManagerComments ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-emerald-600"></div>
              </div>
            ) : unreadManagerComments.length === 0 ? (
              <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-4">
                {t("dashboard:managerCommentsEmpty")}
              </div>
            ) : (
              <div className="space-y-2">
                {unreadManagerComments.slice(0, 10).map((item) => (
                  <div
                    key={item.id}
                    className="bg-gray-50 rounded-lg border border-gray-200 px-4 py-3 flex items-center justify-between gap-4"
                  >
                    <div className="min-w-0 flex-1">
                      {(() => {
                        const timeRangeText = (() => {
                          const s = parseDateTimeString(item.startTime || "");
                          const e = parseDateTimeString(item.endTime || "");
                          const fmtTime = (d: Date) =>
                            d.toLocaleTimeString("tr-TR", {
                              hour: "2-digit",
                              minute: "2-digit",
                            });
                          const fmtDate = (d: Date) =>
                            d.toLocaleDateString("tr-TR");
                          if (s && e) {
                            const sameDay = fmtDate(s) === fmtDate(e);
                            return sameDay
                              ? `${fmtDate(s)} ${fmtTime(s)}-${fmtTime(e)}`
                              : `${fmtDate(s)} ${fmtTime(s)}-${fmtDate(
                                  e
                                )} ${fmtTime(e)}`;
                          }
                          if (s) return `${fmtDate(s)} ${fmtTime(s)}`;
                          if (e) return `${fmtDate(e)} ${fmtTime(e)}`;
                          return null;
                        })();

                        const createdAtText = item.createdAt
                          ? new Date(item.createdAt).toLocaleString("tr-TR", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : null;

                        return (
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm font-semibold text-gray-900 truncate">
                              {(item.projectName || "-") +
                                " / " +
                                (item.activityTitle || "-")}
                            </span>

                            <span className="text-xs text-gray-600 whitespace-nowrap hidden sm:inline">
                              {timeRangeText || createdAtText || "-"}
                            </span>

                            <span className="text-xs text-gray-300 hidden sm:inline">
                              •
                            </span>

                            <span className="text-xs font-semibold text-emerald-700 whitespace-nowrap hidden sm:inline">
                              {item.authorName || "-"}
                            </span>

                            <span className="text-xs text-gray-400 hidden sm:inline">
                              :
                            </span>

                            <span className="text-sm text-gray-700 truncate">
                              {item.comment}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                    <div className="shrink-0 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleOpenManagerCommentDetails(item)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
                      >
                        <EyeIcon className="w-4 h-4" />
                        <span className="hidden sm:inline">
                          {t("common:details")}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMarkManagerCommentRead(item.id)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700"
                      >
                        <CheckCircleIcon className="w-4 h-4" />
                        <span className="hidden sm:inline">
                          {t("dashboard:markAsRead")}
                        </span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Activity History Section - Blue Background */}
        <div className="mt-8 bg-blue-50/50 backdrop-blur-sm rounded-xl border border-blue-200/50 shadow-sm p-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <ClockIcon className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    {t("dashboard:activityHistory")}
                  </h2>
                  <p className="text-xs text-gray-600 mt-1">
                    {t("dashboard:viewAllYourActivities")}
                  </p>
                </div>
              </div>

              {/* Filter and Search */}
              <div className="flex items-center gap-3 flex-wrap">
                {/* Filter: Project Selection */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                    {t("dashboard:filterByProject")}:
                  </label>
                  <select
                    value={historyFilter}
                    onChange={(e) => setHistoryFilter(e.target.value)}
                    className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="all">{t("activities:allActivities")}</option>
                    {getUniqueProjects().map((projectName) => (
                      <option key={projectName} value={projectName}>
                        {projectName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Search - History Activities */}
                <div className="relative w-full sm:w-auto sm:min-w-[200px]">
                  <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t("dashboard:searchActivities")}
                    value={historySearchTerm}
                    onChange={(e) => setHistorySearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm transition-all"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* History Activities List */}
          {loadingHistory ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : filteredHistoryActivities.length === 0 ? (
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-2xl mb-6">
                <ClockIcon className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                {historyFilter === "all"
                  ? t("activities:noActivitiesYet")
                  : t("dashboard:noActivitiesInProject", {
                      project: historyFilter,
                    })}
              </h3>
            </div>
          ) : (
            <div className="space-y-3">
              {paginatedHistoryActivities.map((activity, index) => {
                const gradientClass = getHistoryActivityGradient();
                const isCompleted = activity.status === "COMPLETED";
                const userCompleted = activity.completedUserIds
                  ? (() => {
                      try {
                        const completedIds: number[] = JSON.parse(
                          activity.completedUserIds
                        );
                        return completedIds.includes(user?.id || 0);
                      } catch {
                        return false;
                      }
                    })()
                  : false;

                // Get user specific time info
                const userTimeInfo = (() => {
                  if (!activity.userDurations || !user?.id) return null;
                  try {
                    const durations = JSON.parse(activity.userDurations);
                    const userData = durations[user.id];
                    if (userData && typeof userData === "object") {
                      // Recalculate duration from startTime and endTime for accuracy
                      const breakHours = Array.isArray(userData.breaks)
                        ? calculateBreakHoursFromRanges(
                            userData.breaks,
                            userData.startTime,
                            userData.endTime
                          )
                        : 0;
                      let calculatedDuration = userData.duration || 0;
                      if (userData.startTime && userData.endTime) {
                        const start = parseDateTimeString(userData.startTime);
                        const end = parseDateTimeString(userData.endTime);
                        if (start && end) {
                          const diffMs = end.getTime() - start.getTime();
                          if (diffMs > 0) {
                            const rawHours = diffMs / (1000 * 60 * 60);
                            calculatedDuration = formatDurationHours(
                              Math.max(rawHours - breakHours, 0)
                            );
                          }
                        }
                      } else if (breakHours > 0) {
                        calculatedDuration = formatDurationHours(
                          Math.max(calculatedDuration - breakHours, 0)
                        );
                      }
                      return {
                        startTime: userData.startTime,
                        endTime: userData.endTime,
                        duration: calculatedDuration,
                      };
                    }
                    return null;
                  } catch {
                    return null;
                  }
                })();

                const zeroDurationFallback = !userTimeInfo?.startTime &&
                  !userTimeInfo?.endTime
                  ? getRoundedDateTimeParts(
                      activity.completedAt || activity.startedAt
                    )
                  : null;

                const zeroDurationFallbackDateTime = zeroDurationFallback
                  ? `${zeroDurationFallback.date} ${zeroDurationFallback.time}`
                  : undefined;

                const startTime =
                  userTimeInfo?.startTime ||
                  zeroDurationFallbackDateTime ||
                  activity.startedAt;
                const endTime =
                  userTimeInfo?.endTime ||
                  zeroDurationFallbackDateTime ||
                  activity.completedAt;

                const formatDate = (dateStr?: string) => {
                  if (!dateStr) return "-";
                  return new Date(dateStr).toLocaleDateString("tr-TR", {
                    day: "numeric",
                    month: "short",
                  });
                };

                const formatTime = (dateStr?: string) => {
                  if (!dateStr) return "";
                  return new Date(dateStr).toLocaleTimeString("tr-TR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  });
                };

                return (
                  <div
                    key={activity.id}
                    className="bg-white/70 backdrop-blur-sm rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
                  >
                    <div className="flex items-stretch">
                      {/* Color Strip */}
                      <div
                        className={`w-1.5 bg-gradient-to-b ${gradientClass} flex-shrink-0`}
                      ></div>

                      {/* Main Content */}
                      <div className="flex-1 flex items-center px-4 lg:px-5 py-2 gap-4">
                        {/* Left Section: Icon, Title, Status */}
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {activity.icon && (
                            <div
                              className={`w-10 h-10 bg-gradient-to-br ${gradientClass} rounded-lg flex items-center justify-center flex-shrink-0`}
                            >
                              <span className="text-xl">{activity.icon}</span>
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-xs font-semibold text-gray-900 truncate">
                                {activity.name}
                              </h3>
                              {!(isCompleted || userCompleted) && (
                                <span className="flex-shrink-0 inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                  <ClockIcon className="w-3 h-3" />
                                  <span>
                                    {getActivityStatusLabel(activity.status)}
                                  </span>
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1 flex-wrap text-[11px] text-gray-500">
                              {activity.projectName && (
                                <span className="flex items-center gap-1">
                                  <FolderIcon className="w-3.5 h-3.5 text-gray-400" />
                                  {activity.projectName}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Middle: Time Info */}
                        <div className="flex flex-wrap items-center gap-4 text-[11px] lg:text-xs text-gray-500 border-t lg:border-t-0 lg:border-l border-gray-100 pt-3 lg:pt-0 lg:pl-4">
                          {/* Start Time */}
                          <div
                            className="flex items-center gap-1.5 min-w-[100px]"
                            title={t("dashboard:startedAt")}
                          >
                            <div className="p-1 bg-blue-50 text-blue-600 rounded">
                              <ClockIcon className="w-3.5 h-3.5" />
                            </div>
                            <div className="flex flex-col leading-tight">
                              <span className="font-medium text-gray-700">
                                {formatDate(startTime)}
                              </span>
                              <span className="text-[10px] text-gray-400">
                                {formatTime(startTime) || "--:--"}
                              </span>
                            </div>
                          </div>

                          {/* Arrow */}
                          <div className="hidden sm:block text-gray-300">→</div>

                          {/* End Time */}
                          <div
                            className="flex items-center gap-1.5 min-w-[100px]"
                            title={t("dashboard:completedAt")}
                          >
                            <div className="p-1 bg-green-50 text-green-600 rounded">
                              <CheckCircleIcon className="w-3.5 h-3.5" />
                            </div>
                            <div className="flex flex-col leading-tight">
                              <span className="font-medium text-gray-700">
                                {formatDate(endTime)}
                              </span>
                              <span className="text-[10px] text-gray-400">
                                {formatTime(endTime) || "--:--"}
                              </span>
                            </div>
                          </div>

                          {/* Duration */}
                          {userTimeInfo?.duration ? (
                            <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-lg border border-gray-100 ml-auto lg:ml-0">
                              <span className="font-semibold text-gray-700">
                                {userTimeInfo.duration.toFixed(2)}
                              </span>
                              <span className="text-gray-500 text-[10px] uppercase">
                                {t("dashboard:hours")}
                              </span>
                            </div>
                          ) : null}
                        </div>

                        {/* Right Section: Details */}
                        <div className="flex items-center gap-2 flex-shrink-0 ml-auto lg:ml-0">
                          {canEditHistoryActivity(activity) && (
                            <button
                              onClick={() => handleEditHistoryActivity(activity)}
                              className="p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all"
                              title={t("common:edit")}
                            >
                              <PencilSquareIcon className="w-5 h-5" />
                            </button>
                          )}
                          {canDeleteActivityInstance && (
                            <button
                              onClick={() => handleOpenDeleteConfirm(activity)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                              title={t("common:delete")}
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          )}
                          <button
                            onClick={() => handleViewDetails(activity)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                            title={t("common:viewDetails")}
                          >
                            <EyeIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <PaginationControls
                currentPage={historyActivitiesPage}
                totalPages={historyActivitiesTotalPages}
                totalItems={filteredHistoryActivities.length}
                itemsPerPage={DASHBOARD_HISTORY_PAGE_SIZE}
                onPageChange={setHistoryActivitiesPage}
                accent="purple"
              />
            </div>
          )}
        </div>
      </div>

      {/* Forms Modal */}
      {selectedActivityForms &&
        selectedActivityForms.forms &&
        selectedActivityForms.forms.length > 0 && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-white">
                    {selectedActivityForms.name}
                  </h2>
                  <p className="text-white/80 text-xs mt-1">
                    {selectedActivityForms.forms.length} form
                    {selectedActivityForms.forms.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSelectedActivityForms(null);
                    setSelectSearchByField({});
                  }}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {selectedActivityForms.forms.map((form, index) => (
                  <div
                    key={form.id}
                    className="border border-gray-200 rounded-xl p-6 bg-gray-50"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">
                          {form.title}
                        </h3>
                        {form.description && (
                          <p className="text-xs text-gray-600 mt-1">
                            {form.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {form.isRequired && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                            {t("common:required")}
                          </span>
                        )}
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                          #{form.displayOrder || index + 1}
                        </span>
                      </div>
                    </div>

                    {/* Form Response */}
                    <div className="bg-white rounded-lg p-4 space-y-4">
                      {/* User Response - Show submitted or allow editing */}
                      {form.userResponse &&
                      Object.keys(form.userResponse).length > 0 ? (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                            Your Submitted Response
                          </h4>
                          <div className="space-y-2">
                            {form.formStructure?.elements?.map(
                              (element: any) => {
                                const value = form.userResponse?.[element.id];
                                if (
                                  value === undefined ||
                                  value === null ||
                                  value === ""
                                )
                                  return null;
                                return (
                                  <div
                                    key={element.id}
                                    className="border-l-4 border-green-500 pl-3 py-2 bg-green-50"
                                  >
                                    <div className="text-xs font-medium text-gray-700">
                                      {element.label}
                                    </div>
                                    <div className="text-xs text-gray-900 mt-1">
                                      {typeof value === "object"
                                        ? JSON.stringify(value)
                                        : String(value)}
                                    </div>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-700 mb-3">
                            {t("dashboard:fillForm")}
                          </h4>
                          {form.formStructure?.elements &&
                          Array.isArray(form.formStructure.elements) &&
                          form.formStructure.elements.length > 0 ? (
                            <div className="space-y-3">
                              {form.formStructure.elements.map(
                                (element: any) => (
                                  <div key={element.id}>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      {element.label}
                                      {element.required && (
                                        <span className="text-red-500 ml-1">
                                          *
                                        </span>
                                      )}
                                    </label>
                                    {element.type === "textarea" ? (
                                      <textarea
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        rows={3}
                                        placeholder={
                                          element.placeholder ||
                                          `Enter ${element.label}`
                                        }
                                        value={
                                          formSubmissions[form.id]?.[
                                            element.id
                                          ] || ""
                                        }
                                        onChange={(e) =>
                                          updateFormSubmission(
                                            form.id,
                                            element.id,
                                            e.target.value
                                          )
                                        }
                                      />
                                    ) : element.type === "select" ? (
                                      <div>
                                        <div className="space-y-2 rounded-md border border-gray-300 bg-white p-3 max-h-52 overflow-y-auto">
                                          <input
                                            type="text"
                                            value={
                                              selectSearchByField[
                                                getSelectSearchKey(
                                                  "active",
                                                  selectedActivityForms?.id,
                                                  form.id,
                                                  element.id
                                                )
                                              ] || ""
                                            }
                                            onChange={(event) =>
                                              setSelectSearchByField((prev) => ({
                                                ...prev,
                                                [getSelectSearchKey(
                                                  "active",
                                                  selectedActivityForms?.id,
                                                  form.id,
                                                  element.id
                                                )]: event.target.value,
                                              }))
                                            }
                                            placeholder={t("common:search", "Ara...")}
                                            className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                          />
                                          {(() => {
                                            const selectedValues =
                                              normalizeToStringArray(
                                                formSubmissions[form.id]?.[
                                                  element.id
                                                ]
                                              );
                                            const searchValue =
                                              selectSearchByField[
                                                getSelectSearchKey(
                                                  "active",
                                                  selectedActivityForms?.id,
                                                  form.id,
                                                  element.id
                                                )
                                              ] || "";
                                            const normalizedSearch = searchValue
                                              .trim()
                                              .toLocaleLowerCase("tr-TR");
                                            const filteredOptions =
                                              normalizedSearch.length > 0
                                                ? (element.options || []).filter(
                                                    (option: string) =>
                                                      option
                                                        .toLocaleLowerCase(
                                                          "tr-TR"
                                                        )
                                                        .includes(
                                                          normalizedSearch
                                                        )
                                                  )
                                                : (element.options || []);
                                            if (
                                              (element.options || []).length ===
                                              0
                                            ) {
                                              return (
                                                <div className="text-sm text-gray-500">
                                                  {t(
                                                    "forms:noOptions",
                                                    "Seçenek yok"
                                                  )}
                                                </div>
                                              );
                                            }
                                            if (filteredOptions.length === 0) {
                                              return (
                                                <div className="text-sm text-gray-500">
                                                  {t(
                                                    "common:noResults",
                                                    "Sonuc bulunamadi"
                                                  )}
                                                </div>
                                              );
                                            }
                                            return filteredOptions.map(
                                              (option: string, optIndex: number) => (
                                                <label
                                                  key={optIndex}
                                                  className="flex items-center"
                                                >
                                                  <input
                                                    type="checkbox"
                                                    checked={selectedValues.includes(option)}
                                                    onChange={(e) => {
                                                      const nextValues = e.target.checked
                                                        ? [...selectedValues, option]
                                                        : selectedValues.filter(
                                                            (item) =>
                                                              item !== option
                                                          );
                                                      updateFormSubmission(
                                                        form.id,
                                                        element.id,
                                                        normalizeSelectSubmissionValue(
                                                          nextValues
                                                        )
                                                      );
                                                    }}
                                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                  />
                                                  <span className="ml-2 text-sm text-gray-700">
                                                    {option}
                                                  </span>
                                                </label>
                                              )
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    ) : (
                                      <input
                                        type={element.type || "text"}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder={
                                          element.placeholder ||
                                          `Enter ${element.label}`
                                        }
                                        value={
                                          formSubmissions[form.id]?.[
                                            element.id
                                          ] || ""
                                        }
                                        onChange={(e) =>
                                          updateFormSubmission(
                                            form.id,
                                            element.id,
                                            e.target.value
                                          )
                                        }
                                      />
                                    )}
                                  </div>
                                )
                              )}
                              <button
                                onClick={() => handleSubmitForm(form.id)}
                                className="w-full mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center space-x-2"
                              >
                                <CheckIcon className="w-5 h-5" />
                                <span>{t("dashboard:submitForm")}</span>
                              </button>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 italic">
                              {t("dashboard:noFormFields")}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Modal Footer */}
              <div className="border-t p-4 bg-gray-50">
                <button
                  onClick={() => {
                    setSelectedActivityForms(null);
                    setSelectSearchByField({});
                  }}
                  className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors"
                >
                  {t("common:close")}
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Attachments Modal */}
      {selectedActivityAttachments && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">
                  {selectedActivityAttachments.activity.name}
                </h2>
                <p className="text-white/80 text-sm mt-1">
                  {selectedActivityAttachments.attachments.length} dosya
                  {selectedActivityAttachments.attachments.length !== 1
                    ? "lar"
                    : ""}
                </p>
              </div>
              <button
                onClick={() => setSelectedActivityAttachments(null)}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {selectedActivityAttachments.attachments.map((attachment) => (
                  <button
                    key={attachment.id}
                    type="button"
                    onClick={() =>
                      void handlePreviewWorkflowAttachment(attachment)
                    }
                    disabled={previewingDriveFileId === attachment.id}
                    className="flex flex-col p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all group"
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      {previewingDriveFileId === attachment.id ? (
                        <ArrowPathIcon className="w-8 h-8 text-blue-500 flex-shrink-0 animate-spin" />
                      ) : attachment.iconUrl ? (
                        <img
                          src={attachment.iconUrl}
                          alt=""
                          className="w-8 h-8 flex-shrink-0"
                        />
                      ) : (
                        <DocumentTextIcon className="w-8 h-8 text-gray-400 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate group-hover:text-blue-600">
                          {attachment.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {attachment.mimeType?.split("/")[1]?.toUpperCase() ||
                            "FILE"}
                        </p>
                      </div>
                    </div>
                    <div className="text-xs text-gray-400">
                      {t("dashboard:clickToDownload")}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t p-4 bg-gray-50">
              <button
                onClick={() => setSelectedActivityAttachments(null)}
                className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors"
              >
                {t("common:close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duration Modal */}
      {showDurationModal && selectedActivityForDuration && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in slide-in-from-top-4 duration-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <ClockIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">
                      {t("dashboard:activityDuration")}
                    </h3>
                    <p className="text-orange-100 text-sm mt-1">
                      {selectedActivityForDuration.name}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowDurationModal(false);
                    setSelectedActivityForDuration(null);
                    setDurationInput("");
                    setDurationError("");
                  }}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {(() => {
                const currentDuration = getUserDuration(
                  selectedActivityForDuration,
                  user!.id
                );
                return currentDuration > 0 ? (
                  <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg mb-4">
                    <div className="flex items-center gap-2">
                      <CheckIcon className="h-5 w-5 text-green-600" />
                      <p className="text-sm text-green-700 font-medium">
                        {t("dashboard:currentDuration", {
                          hours: currentDuration,
                        })}{" "}
                        <strong>
                          ({currentDuration} {t("dashboard:hours")})
                        </strong>
                      </p>
                    </div>
                    <p className="text-xs text-green-600 mt-1">
                      {t("dashboard:updateDurationHint")}
                    </p>
                  </div>
                ) : null;
              })()}

              <p className="text-gray-700 mb-4">
                {t("dashboard:durationQuestion")}
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("dashboard:durationHours")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={durationInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDurationInput(value);

                      // Validate duration input
                      if (value && parseFloat(value) <= 0) {
                        setDurationError(t("dashboard:invalidDuration"));
                      } else if (value && parseFloat(value) > 0) {
                        setDurationError("");
                      }
                    }}
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        durationInput &&
                        parseFloat(durationInput) > 0
                      ) {
                        handleSaveDuration();
                      }
                    }}
                    placeholder={t("dashboard:durationExample")}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 text-lg ${
                      durationError
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300 focus:ring-orange-500 focus:border-transparent"
                    }`}
                    autoFocus
                  />
                  {durationError && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <span>⚠️</span>
                      <span>{durationError}</span>
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    💡 {t("dashboard:durationExamples")}
                  </p>
                </div>

                <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded-r-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <ClockIcon className="h-5 w-5 text-orange-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-orange-700">
                        {t("dashboard:durationInfo")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDurationModal(false);
                  setSelectedActivityForDuration(null);
                  setDurationInput("");
                  setDurationError("");
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                {t("common:cancel")}
              </button>
              <button
                onClick={handleSaveDuration}
                disabled={!durationInput || parseFloat(durationInput) <= 0}
                className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg shadow-sm hover:shadow-md transition-all font-bold text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <CheckIcon className="w-5 h-5" />
                {t("dashboard:saveDuration")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unified Complete Activity Modal */}
      {showCompleteModal && activityToCompleteData && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">
                  {activityToCompleteData.name}
                </h2>
                <p className="text-white/80 text-sm mt-1">
                  {isEditMode
                    ? t("activities:editActivity")
                    : t("dashboard:completeActivity")}
                </p>
              </div>
              <button
                onClick={resetCompleteModalState}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Duration Section */}
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                    <ClockIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t("dashboard:activityDuration")}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {completeModalAllowsZeroDuration
                        ? t("dashboard:enterStartEndTimeOptionalDesc")
                        : t("dashboard:enterStartEndTimeDesc")}
                    </p>
                  </div>
                  {completeModalAllowsZeroDuration && (
                    <span className="inline-flex items-center rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700">
                      {t("common:optional")}
                    </span>
                  )}
                </div>
                <div className="space-y-4">
                  {/* Start Date and Time */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("dashboard:startDate")}{" "}
                        {!completeModalAllowsZeroDuration && (
                          <span className="text-red-500">*</span>
                        )}
                      </label>
                      <input
                        type="date"
                        value={completeModalStartDate}
                        onChange={(e) =>
                          setCompleteModalStartDate(e.target.value)
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("dashboard:startTime")}{" "}
                        {!completeModalAllowsZeroDuration && (
                          <span className="text-red-500">*</span>
                        )}
                      </label>
                      <select
                        value={completeModalStartTime}
                        onChange={(e) =>
                          setCompleteModalStartTime(e.target.value)
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                      >
                        <option value="">{t("dashboard:selectTime")}</option>
                        {generateTimeOptions().map((time) => (
                          <option key={time} value={time}>
                            {time}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* End Date and Time */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("dashboard:endDate")}{" "}
                        {!completeModalAllowsZeroDuration && (
                          <span className="text-red-500">*</span>
                        )}
                      </label>
                      <input
                        type="date"
                        value={completeModalEndDate}
                        onChange={(e) =>
                          setCompleteModalEndDate(e.target.value)
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t("dashboard:endTime")}{" "}
                        {!completeModalAllowsZeroDuration && (
                          <span className="text-red-500">*</span>
                        )}
                      </label>
                      <select
                        value={completeModalEndTime}
                        onChange={(e) =>
                          setCompleteModalEndTime(e.target.value)
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg"
                      >
                        <option value="">{t("dashboard:selectTime")}</option>
                        {generateTimeOptions().map((time) => (
                          <option key={time} value={time}>
                            {time}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Breaks Section */}
                  <div className="border-t border-orange-200 pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">
                          {t("dashboard:breaksTitle")}
                        </h4>
                        <p className="text-xs text-gray-600">
                          {t("dashboard:breaksDesc")}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={addBreakEntry}
                        disabled={!isBreaksReady}
                        className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold text-orange-700 bg-orange-100 rounded-lg transition-colors ${
                          isBreaksReady
                            ? "hover:bg-orange-200"
                            : "opacity-60 cursor-not-allowed"
                        }`}
                      >
                        <PlusIcon className="w-4 h-4" />
                        {t("dashboard:addBreak")}
                      </button>
                    </div>

                    {completeModalBreaks.length > 0 && (
                      <div className="mt-4 space-y-3">
                        {completeModalBreaks.map((entry, index) => (
                          <div
                            key={`break-${index}`}
                            className="bg-white border border-orange-100 rounded-lg p-4"
                          >
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-xs font-semibold text-gray-700">
                                {t("dashboard:breakItem")} {index + 1}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeBreakEntry(index)}
                                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-600"
                              >
                                <TrashIcon className="w-4 h-4" />
                                {t("common:remove")}
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  {t("dashboard:startTime")}
                                </label>
                                <select
                                  value={entry.startTime}
                                  onChange={(e) =>
                                    updateBreakEntry(
                                      index,
                                      "startTime",
                                      e.target.value
                                    )
                                  }
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                                >
                                  <option value="">{t("dashboard:selectTime")}</option>
                                  {getBreakTimeOptions().map((time) => (
                                    <option key={time} value={time}>
                                      {time}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  {t("dashboard:endTime")}
                                </label>
                                <select
                                  value={entry.endTime}
                                  onChange={(e) =>
                                    updateBreakEntry(
                                      index,
                                      "endTime",
                                      e.target.value
                                    )
                                  }
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                                >
                                  <option value="">{t("dashboard:selectTime")}</option>
                                  {getBreakTimeOptions().map((time) => (
                                    <option key={time} value={time}>
                                      {time}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Calculated Duration Display */}
                  {completeModalStartTime &&
                    completeModalEndTime &&
                    completeModalStartDate &&
                    completeModalEndDate && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">
                            {t("dashboard:calculatedDuration")}:
                          </span>
                          <span className="text-xl font-bold text-green-700">
                            {formatDurationHours(
                              getNetDurationWithBreaks(
                                completeModalStartDate,
                                completeModalStartTime,
                                completeModalEndDate,
                                completeModalEndTime,
                                completeModalBreaks
                              ).netHours
                            )}{" "}
                            {t("dashboard:hours")}
                          </span>
                        </div>
                        {completeModalBreaks.length > 0 && (
                          <div className="mt-2 text-xs text-gray-600 flex items-center justify-between">
                            <span>{t("dashboard:breakTotal")}:</span>
                            <span className="font-semibold text-orange-700">
                              {formatDurationHours(
                                getNetDurationWithBreaks(
                                  completeModalStartDate,
                                  completeModalStartTime,
                                  completeModalEndDate,
                                  completeModalEndTime,
                                  completeModalBreaks
                                ).breakHours
                              )}{" "}
                              {t("dashboard:hours")}
                            </span>
                          </div>
                        )}
                        {completeModalBreaks.length > 0 && (
                          <div className="mt-1 text-xs text-gray-600 flex items-center justify-between">
                            <span>{t("dashboard:netDuration")}:</span>
                            <span className="font-semibold text-green-700">
                              {formatDurationHours(
                                getNetDurationWithBreaks(
                                  completeModalStartDate,
                                  completeModalStartTime,
                                  completeModalEndDate,
                                  completeModalEndTime,
                                  completeModalBreaks
                                ).netHours
                              )}{" "}
                              {t("dashboard:hours")}
                            </span>
                          </div>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          {completeModalStartDate} {completeModalStartTime} -{" "}
                          {completeModalEndDate} {completeModalEndTime}
                        </p>
                      </div>
                    )}

                  {/* Show existing time data if any */}
                  {(() => {
                    const currentTimeData = getUserTimeData(
                      activityToCompleteData,
                      user!.id
                    );
                    const previousBreakHours =
                      currentTimeData?.breaks && currentTimeData.breaks.length
                        ? calculateBreakHoursFromRanges(
                            currentTimeData.breaks,
                            currentTimeData.startTime,
                            currentTimeData.endTime
                          )
                        : 0;
                    return currentTimeData && currentTimeData.duration > 0 ? (
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r-lg">
                        <p className="text-sm text-blue-700">
                          {t("dashboard:previousTimeEntry")}:{" "}
                          {currentTimeData.startTime || "-"} -{" "}
                          {currentTimeData.endTime || "-"} (
                          {currentTimeData.duration} {t("dashboard:hours")}
                          {previousBreakHours > 0
                            ? `, ${t("dashboard:breakTotal")}: ${formatDurationHours(
                                previousBreakHours
                              )} ${t("dashboard:hours")}`
                            : ""}
                          )
                        </p>
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>

              {/* Forms Section */}
              {activityToCompleteData.forms &&
                activityToCompleteData.forms.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                        <DocumentTextIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {t("dashboard:forms")}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {t("dashboard:formCount", {
                            count: activityToCompleteData.forms.length,
                          })}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {activityToCompleteData.forms.map((form, index) => {
                        const formData =
                          completeModalFormSubmissions[form.id] ||
                          getExistingFormResponse(activityToCompleteData, form) ||
                          {};

                        return (
                          <div
                            key={form.id}
                            className="bg-white border border-gray-200 rounded-lg p-4"
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h4 className="text-base font-semibold text-gray-900">
                                  {form.title}
                                </h4>
                                {form.description && (
                                  <p className="text-sm text-gray-600 mt-1">
                                    {form.description}
                                  </p>
                                )}
                              </div>
                              {form.isRequired && (
                                <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                                  {t("common:required")}
                                </span>
                              )}
                            </div>

                            {form.formStructure?.elements &&
                            Array.isArray(form.formStructure.elements) &&
                            form.formStructure.elements.length > 0 ? (
                              <div className="space-y-3">
                                {form.formStructure.elements.map(
                                  (element: any) => (
                                    <div key={element.id}>
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {element.label}
                                        {element.required && (
                                          <span className="text-red-500 ml-1">
                                            *
                                          </span>
                                        )}
                                      </label>
                                      {element.type === "textarea" ? (
                                        <textarea
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                          rows={3}
                                          placeholder={
                                            element.placeholder ||
                                            `Enter ${element.label}`
                                          }
                                          value={formData[element.id] || ""}
                                          onChange={(e) =>
                                            updateCompleteModalFormSubmission(
                                              form.id,
                                              element.id,
                                              e.target.value
                                            )
                                          }
                                        />
                                      ) : element.type === "select" ? (
                                        <div>
                                          <div className="space-y-2 rounded-md border border-gray-300 bg-white p-3 max-h-52 overflow-y-auto">
                                            <input
                                              type="text"
                                              value={
                                                selectSearchByField[
                                                  getSelectSearchKey(
                                                    "complete",
                                                    activityToCompleteData?.id,
                                                    form.id,
                                                    element.id
                                                  )
                                                ] || ""
                                              }
                                              onChange={(event) =>
                                                setSelectSearchByField((prev) => ({
                                                  ...prev,
                                                  [getSelectSearchKey(
                                                    "complete",
                                                    activityToCompleteData?.id,
                                                    form.id,
                                                    element.id
                                                  )]: event.target.value,
                                                }))
                                              }
                                              placeholder={t(
                                                "common:search",
                                                "Ara..."
                                              )}
                                              className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                            {(() => {
                                              const selectedValues =
                                                normalizeToStringArray(
                                                  formData[element.id]
                                                );
                                              const searchValue =
                                                selectSearchByField[
                                                  getSelectSearchKey(
                                                    "complete",
                                                    activityToCompleteData?.id,
                                                    form.id,
                                                    element.id
                                                  )
                                                ] || "";
                                              const normalizedSearch = searchValue
                                                .trim()
                                                .toLocaleLowerCase("tr-TR");
                                              const filteredOptions =
                                                normalizedSearch.length > 0
                                                  ? (element.options || []).filter(
                                                      (option: string) =>
                                                        option
                                                          .toLocaleLowerCase(
                                                            "tr-TR"
                                                          )
                                                          .includes(
                                                            normalizedSearch
                                                          )
                                                    )
                                                  : (element.options || []);
                                              if (
                                                (element.options || []).length ===
                                                0
                                              ) {
                                                return (
                                                  <div className="text-sm text-gray-500">
                                                    {t(
                                                      "forms:noOptions",
                                                      "Seçenek yok"
                                                    )}
                                                  </div>
                                                );
                                              }
                                              if (filteredOptions.length === 0) {
                                                return (
                                                  <div className="text-sm text-gray-500">
                                                    {t(
                                                      "common:noResults",
                                                      "Sonuc bulunamadi"
                                                    )}
                                                  </div>
                                                );
                                              }
                                              return filteredOptions.map(
                                                (option: string, optIndex: number) => (
                                                  <label
                                                    key={optIndex}
                                                    className="flex items-center"
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      checked={selectedValues.includes(option)}
                                                      onChange={(e) => {
                                                        const nextValues = e.target.checked
                                                          ? [...selectedValues, option]
                                                          : selectedValues.filter(
                                                              (item) =>
                                                                item !== option
                                                            );
                                                        updateCompleteModalFormSubmission(
                                                          form.id,
                                                          element.id,
                                                          normalizeSelectSubmissionValue(
                                                            nextValues
                                                          )
                                                        );
                                                      }}
                                                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                    />
                                                    <span className="ml-2 text-sm text-gray-700">
                                                      {option}
                                                    </span>
                                                  </label>
                                                )
                                              );
                                            })()}
                                          </div>
                                        </div>
                                      ) : (
                                        <input
                                          type={element.type || "text"}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                          placeholder={
                                            element.placeholder ||
                                            `Enter ${element.label}`
                                          }
                                          value={formData[element.id] || ""}
                                          onChange={(e) =>
                                            updateCompleteModalFormSubmission(
                                              form.id,
                                              element.id,
                                              e.target.value
                                            )
                                          }
                                        />
                                      )}
                                    </div>
                                  )
                                )}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 italic">
                                {t("dashboard:noFormFields")}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              {/* Submit Description Section */}
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                    <DocumentTextIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {submitDescriptionLabel}
                    </h3>
                    <p className="text-sm text-gray-600">
                      Bu aktivite hakkında eklemek istediğiniz notlar (İsteğe
                      Bağlı)
                    </p>
                  </div>
                </div>

                

                <textarea
                  value={completeModalSubmitDescription}
                  onChange={(e) =>
                    setCompleteModalSubmitDescription(e.target.value)
                  }
                  rows={3}
                  className="w-full px-4 py-3 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  placeholder={submitDescriptionPlaceholder}
                />
              </div>

              {isEditMode && completeModalExistingAttachments.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-base font-semibold text-gray-800 flex items-center space-x-2">
                      <PaperClipIcon className="w-5 h-5 text-green-600" />
                      <span>
                        {t("dashboard:attachments")} (
                        {completeModalExistingAttachments.length})
                      </span>
                    </label>
                  </div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {completeModalExistingAttachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="bg-gray-50 p-3 rounded-lg border border-gray-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3 min-w-0">
                            {attachment.iconUrl ? (
                              <img
                                src={attachment.iconUrl}
                                alt=""
                                className="w-6 h-6 flex-shrink-0"
                              />
                            ) : (
                              <DocumentTextIcon className="w-6 h-6 text-blue-500 flex-shrink-0" />
                            )}
                            <div className="min-w-0">
                              <button
                                type="button"
                                onClick={() =>
                                  void handlePreviewWorkflowAttachment(attachment)
                                }
                                disabled={previewingDriveFileId === attachment.id}
                                className="text-sm font-medium text-gray-700 hover:text-blue-600 truncate block"
                              >
                                {attachment.name}
                              </button>
                              {attachment.description && (
                                <p className="text-xs text-gray-500 truncate">
                                  {attachment.description}
                                </p>
                              )}
                            </div>
                          </div>
                          {(isAdmin() || isManager()) && (
                            <button
                              onClick={() =>
                                handleRemoveExistingAttachment(attachment.id)
                              }
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors ml-2"
                              title={t("common:remove")}
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* File Upload Section */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-base font-semibold text-gray-800 flex items-center space-x-2">
                    <PaperClipIcon className="w-5 h-5 text-green-600" />
                    <span>{t("dashboard:attachFiles")}</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => completeModalFolderInputRef.current?.click()}
                      className="flex items-center space-x-2 text-sm text-amber-700 hover:text-amber-800 font-medium bg-amber-50 hover:bg-amber-100 px-3 py-1.5 rounded-lg transition-colors"
                      disabled={uploadingFiles || !activityToCompleteData}
                    >
                      <FolderIcon className="w-4 h-4" />
                      <span>{t("dashboard:uploadProjectFiles.folderFromComputer")}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDrivePicker(true)}
                      className="flex items-center space-x-2 text-sm text-green-600 hover:text-green-700 font-medium bg-green-50 hover:bg-green-100 px-3 py-1.5 rounded-lg transition-colors"
                      disabled={uploadingFiles || !activityToCompleteData}
                    >
                      <PlusIcon className="w-4 h-4" />
                      <span>{t("dashboard:uploadProjectFiles.addFromDrive")}</span>
                    </button>
                  </div>
                </div>

                <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {t("dashboard:uploadProjectFiles.targetFolder")}
                    </p>
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {completeModalSelectedFolderPath}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {t("dashboard:uploadProjectFiles.folderFromComputerHint")}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowCompleteFolderSelectModal(true)}
                      className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      disabled={!activityToCompleteData || uploadingFiles}
                    >
                      {t("dashboard:uploadProjectFiles.selectFolder")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCompleteModalSelectedFolderId(null);
                        const rootLabel = getCompleteModalRootLabel(
                          activityToCompleteData
                        );
                        setCompleteModalSelectedFolderPath(rootLabel);
                        setCompleteModalSelectedFolderPathEntries(
                          buildCompleteModalRootPathEntries(rootLabel)
                        );
                      }}
                      className="px-3 py-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      disabled={uploadingFiles}
                      title={t("dashboard:uploadProjectFiles.backToRootFolder")}
                    >
                      {t("dashboard:uploadProjectFiles.reset")}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-center w-full mb-4">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => completeModalFileInputRef.current?.click()}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        completeModalFileInputRef.current?.click();
                      }
                    }}
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-green-300 border-dashed rounded-xl cursor-pointer bg-green-50 hover:bg-green-100 transition-colors"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <svg
                        className="w-8 h-8 mb-3 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">
                          {t("dashboard:clickToUpload")}
                        </span>
                      </p>
                      <p className="text-xs text-gray-500">
                        {t("dashboard:uploadFileTypes")}
                      </p>
                    </div>
                  </div>
                </div>

                <input
                  ref={completeModalFileInputRef}
                  type="file"
                  className="hidden"
                  multiple
                  onChange={handleCompleteModalFileSelect}
                  disabled={uploadingFiles || !activityToCompleteData}
                />
                <input
                  ref={completeModalFolderInputRef}
                  type="file"
                  className="hidden"
                  multiple
                  onChange={handleCompleteModalFolderSelect}
                  disabled={uploadingFiles || !activityToCompleteData}
                  {...({ webkitdirectory: "", directory: "" } as any)}
                />

                <ProjectFolderSelectModal
                  isOpen={showCompleteFolderSelectModal}
                  onClose={() => setShowCompleteFolderSelectModal(false)}
                  workflowInstanceId={activityToCompleteData?.workflowInstanceId || null}
                  initialFolderId={completeModalSelectedFolderId}
                  initialPath={completeModalSelectedFolderPathEntries}
                  rootLabel={getCompleteModalRootLabel(activityToCompleteData)}
                  onSelect={(folderId, path) => {
                    setCompleteModalSelectedFolderId(folderId);
                    setCompleteModalSelectedFolderPathEntries(path);
                    const label = (path || [])
                      .map((p) => p.name)
                      .filter(Boolean)
                      .join(" / ");
                    setCompleteModalSelectedFolderPath(
                      label || getCompleteModalRootLabel(activityToCompleteData)
                    );
                  }}
                />

                {/* Selected Files List */}
                {(completeModalFiles.length > 0 ||
                  completeModalDriveAttachments.length > 0) && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-700">
                      {t("dashboard:selectedFiles")} (
                      {completeModalFiles.length +
                        completeModalDriveAttachments.length}
                      )
                    </h4>
                    <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                      {completeModalFiles.map((selectedFile) => (
                        <div
                          key={selectedFile.id}
                          className="bg-white p-3 rounded-lg border border-green-100 hover:border-green-300 hover:shadow-sm transition-all space-y-2"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <DocumentTextIcon className="w-8 h-8 text-blue-500 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-700 truncate max-w-xs">
                                  {selectedFile.file.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {(selectedFile.file.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                                {getCompleteModalLocalFileDisplayPath(
                                  selectedFile.file
                                ) !== selectedFile.file.name && (
                                  <p className="text-[11px] text-gray-400 truncate">
                                    {getCompleteModalLocalFileDisplayPath(
                                      selectedFile.file
                                    )}
                                  </p>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => removeCompleteModalFile(selectedFile.id)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors ml-2"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                            <div className="min-w-0">
                              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                {t("dashboard:uploadProjectFiles.fileFolder")}
                              </p>
                              <p className="text-xs text-gray-700 truncate">
                                {getCompleteModalFileTargetFolderDisplayPath(
                                  selectedFile
                                )}
                              </p>
                            </div>
                          </div>

                          <input
                            type="text"
                            placeholder={t("dashboard:uploadProjectFiles.optionalDescription")}
                            value={completeModalFileDescriptions[selectedFile.id] || ""}
                            onChange={(e) =>
                              setCompleteModalFileDescriptions((prev) => ({
                                ...prev,
                                [selectedFile.id]: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            disabled={uploadingFiles}
                          />
                        </div>
                      ))}
                      {completeModalDriveAttachments.map((att) => (
                        <div
                          key={`drive-${att.id}`}
                          className="bg-white p-3 rounded-lg border border-blue-100 hover:border-green-300 hover:shadow-sm transition-all space-y-2"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              {att.iconUrl ? (
                                <img
                                  src={att.iconUrl}
                                  alt=""
                                  className="w-8 h-8 object-contain flex-shrink-0"
                                />
                              ) : (
                                <DocumentTextIcon className="w-8 h-8 text-green-600 flex-shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-700 truncate max-w-xs">
                                  {att.name}
                                </p>
                                <div className="flex items-center space-x-2 text-xs text-gray-500">
                                  <span className="text-green-600 font-medium">
                                    Google Drive
                                  </span>
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => removeCompleteModalDriveFile(att.id)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors ml-2"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                            <div className="min-w-0">
                              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                {t("dashboard:uploadProjectFiles.fileFolder")}
                              </p>
                              <p className="text-xs text-gray-700 truncate">
                                {att.targetFolderPath ||
                                  getCompleteModalRootLabel(activityToCompleteData)}
                              </p>
                            </div>
                            <FolderIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          </div>

                          <input
                            type="text"
                            placeholder={t("dashboard:uploadProjectFiles.optionalDescription")}
                            value={completeModalFileDescriptions[att.id] || ""}
                            onChange={(e) =>
                              setCompleteModalFileDescriptions((prev) => ({
                                ...prev,
                                [att.id]: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            disabled={uploadingFiles}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t p-6 bg-gray-50 flex items-center justify-end space-x-3">
              <button
                onClick={resetCompleteModalState}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                {t("common:cancel")}
              </button>
              <button
                onClick={
                  isEditMode ? handleEditModalSubmit : handleCompleteModalSubmit
                }
                disabled={
                  uploadingFiles ||
                  !activityToCompleteData ||
                  (!completeModalAllowsZeroDuration &&
                    !completeModalHasValidTimeRange) ||
                  (completeModalAllowsZeroDuration &&
                    completeModalHasTimeEntry &&
                    !completeModalHasValidTimeRange)
                }
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg shadow-sm hover:shadow-md transition-all font-bold text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {uploadingFiles ? (
                  <>
                    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span>{t("dashboard:uploading")}</span>
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="w-5 h-5" />
                    <span>
                      {isEditMode ? t("common:save") : t("dashboard:complete")}
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <TaskDetailModal
        isOpen={!!selectedHistoryActivity}
        onClose={() => setSelectedHistoryActivity(null)}
        task={selectedHistoryActivity as any}
      />

      {descriptionInfoModal && (
        <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 truncate pr-4">
                {descriptionInfoModal.title}
              </h3>
              <button
                type="button"
                onClick={() => setDescriptionInfoModal(null)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
              <p className="text-sm text-gray-700 whitespace-normal break-words">
                {descriptionInfoModal.description}
              </p>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                onClick={() => setDescriptionInfoModal(null)}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                {t("common:close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Activity Confirmation Modal */}
      {showDeleteConfirm && activityToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in slide-in-from-top-4 duration-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 p-6">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <TrashIcon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white">
                    {t("activities:deleteActivity")}
                  </h3>
                  <p className="text-red-100 text-sm mt-1">
                    {activityToDelete.name}
                  </p>
                </div>
                <button
                  onClick={handleCancelDeleteActivity}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700">
                {t("activities:confirmDeleteActivity")}
              </p>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3">
              <button
                onClick={handleCancelDeleteActivity}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                {t("common:cancel")}
              </button>
              <button
                onClick={handleConfirmDeleteActivity}
                className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg shadow-sm hover:shadow-md transition-all font-bold"
              >
                {t("common:delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Activity Confirmation Modal */}
      {showCompleteConfirm && activityToComplete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in slide-in-from-top-4 duration-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 p-6">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <CheckCircleIcon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white">
                    {t("dashboard:completeActivity")}
                  </h3>
                  <p className="text-green-100 text-sm mt-1">
                    {t("dashboard:confirmCompleteActivityTitle")}
                  </p>
                </div>
                <button
                  onClick={cancelCompleteActivity}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                {t("dashboard:confirmCompleteActivity", {
                  activityName: activityToComplete.name,
                  duration: activityToComplete.duration,
                })}
              </p>
              {activityToComplete.formCount > 0 && (
                <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg mb-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <DocumentTextIcon className="h-5 w-5 text-green-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-green-700 font-semibold">
                        📝{" "}
                        {t("dashboard:formCount", {
                          count: activityToComplete.formCount,
                        })}{" "}
                        {t("dashboard:formsFilled")}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ClockIcon className="h-5 w-5 text-blue-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700 font-semibold mb-1">
                      {t("dashboard:activityDuration")}
                    </p>
                    <p className="text-base text-blue-900">
                      {activityToComplete.duration} {t("dashboard:hours")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3">
              <button
                onClick={cancelCompleteActivity}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                {t("common:cancel")}
              </button>
              <button
                onClick={confirmCompleteActivity}
                className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg shadow-sm hover:shadow-md transition-all font-medium flex items-center space-x-2"
              >
                <CheckCircleIcon className="w-5 h-5" />
                <span>{t("dashboard:complete")}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Activity Modal */}
      <AddActivityModal
        isOpen={showAddActivityModal}
        onClose={() => setShowAddActivityModal(false)}
        onSuccess={async () => {
          toast.success(t("dashboard:activityAdded"));
          await Promise.all([loadDashboardData(), loadActivityHistory()]);
        }}
        onDailyHoursUpdate={(status) => applyDailyHoursUpdate(status)}
        userId={user?.id || 0}
      />

      {/* Upload Files to Project Modal */}
      <UploadFilesToProjectModal
        isOpen={showUploadFilesModal}
        onClose={() => setShowUploadFilesModal(false)}
        onSuccess={() => {
          toast.success(t("dashboard:filesUploaded"));
          setShowUploadFilesModal(false);
        }}
        userId={user?.id || 0}
      />

      <GoogleDriveFilePicker
        isOpen={showDrivePicker}
        onClose={() => setShowDrivePicker(false)}
        onSelect={(files) => {
          setCompleteModalDriveAttachments((prev) => [
            ...prev,
            ...buildSelectedCompleteModalDriveAttachments(files),
          ]);
          setShowDrivePicker(false);
        }}
      />
    </div>
  );
};







